import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useListBrandTemplates } from "@dataconnect/generated/react";
import CustomSelect from "../components/CustomSelect";
import { auth } from "../config/firebase";
import {
  callAnalysis,
  callGenerateImage,
  snapshotGenerationContext,
} from "../services/backendAiService";
import { uploadImageToStorage } from "../services/studioPersistenceService";
import {
  generateDecorationImage,
  type DecorationGenerationDebugPayload,
} from "../services/decorationGenerationService";
import type { BrandIdentity, Vibe } from "../types";
import { deserializeBrandTemplate } from "../utils/brandTemplateHelpers";
import {
  DEFAULT_ROOM_W,
  DEFAULT_ROOM_H,
  DEFAULT_ROOM_D,
  CANVAS_PX,
  SCENE_OBJECT_DEFINITIONS,
  buildEmptyRoomAutoStagePlan,
  buildSceneBox,
  cameraBasis,
  clampSceneObjectOrigin,
  cmToPixels,
  defaultCameraForRoom,
  drawBox3D,
  drawEmptyRoomFurniture,
  drawLine3D,
  drawRoom3D as drawRoom3DBase,
  fillFace3D,
  getSceneObjectDefinition,
  getSceneObjectSize,
  loadImage,
  mapSceneToInteriorType,
  pickBestMappedOption,
  project,
  sceneBoxToSurface,
  trimTransparentPadding,
  v3Add,
  v3Norm,
  v3Scale,
  type Camera3D,
  type FramingPreference,
  type InteriorType,
  type ProductPosition3D,
  type ProductStagingProfile,
  type RoomDimensions,
  type RoomPlacementPreference,
  type RoomViewAngle,
  type SceneBox,
  type SceneObjectDefinition,
  type SceneObjectState,
  type SceneWall,
  type SnapSurface,
  type StagingSurfaceOverride,
  type Vec3,
} from "../utils/room3d";

export const Route = createFileRoute("/demo/scaling")({
  component: DemoScalingPage,
});

const ROOM_W = DEFAULT_ROOM_W;
const ROOM_H = DEFAULT_ROOM_H;
const ROOM_D = DEFAULT_ROOM_D;

// ---------------------------------------------------------------------------
// GCS upload helper — ensures all images sent to the backend go through GCS
// ---------------------------------------------------------------------------

async function uploadDataUrlToGCS(
  dataUrl: string,
  storagePath: string,
  mimeType = "image/png"
): Promise<string> {
  const blob = await (await fetch(dataUrl)).blob();
  await uploadImageToStorage(blob, storagePath, { contentType: mimeType });
  return storagePath;
}

function buildTempPath(label: string): string {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User not authenticated");
  return `f/${userId}/tmp/${label}_${Date.now()}.png`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LogEntry {
  time: string;
  label: string;
  detail?: string;
  imageDataUrl?: string;
}

interface ApiDebugAsset {
  label: string;
  filename: string;
  mimeType: string;
  dataUrl: string;
}

interface ApiDebugPayload {
  request: {
    mode: "free_prompt";
    images: Record<string, string | string[] | undefined>;
    params: Record<string, unknown>;
  };
  prompt: string;
  assets: ApiDebugAsset[];
}

function toApiDebugPayload(
  debug: DecorationGenerationDebugPayload
): ApiDebugPayload {
  return {
    request: debug.request,
    prompt:
      typeof debug.request.params.prompt === "string"
        ? debug.request.params.prompt
        : "",
    assets: [
      {
        label: "Placement Sketch",
        filename: "image-1-sketch.png",
        mimeType: "image/png",
        dataUrl: debug.assets.sketchDataUrl,
      },
      {
        label: "Placement Composite",
        filename: "image-1-placement.png",
        mimeType: "image/png",
        dataUrl: debug.assets.compositeDataUrl,
      },
      {
        label: "Product",
        filename: "image-2-product.png",
        mimeType: "image/png",
        dataUrl: debug.assets.productReferenceDataUrl,
      },
    ],
  };
}

interface NormalizedRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CustomRoomSurface {
  id: string;
  label: string;
  surfaceType: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CustomRoomPlan {
  styleLabel: string;
  roomType: string;
  roomContext: string;
  lineArtPrompt: string;
  estimatedRoomDimensionsCm: {
    width: number;
    height: number;
    depth: number;
    confidence: string;
    reasoning: string;
  };
  placement: {
    surfaceType: string;
    surfaceLabel: string;
    reason: string;
    rect: NormalizedRect;
    estimatedProductScaleNote: string;
  };
  camera: {
    rect: NormalizedRect;
    reason: string;
    subjectFraming: string;
  };
  surfaces: CustomRoomSurface[];
}

interface ProductPlacementProfile {
  mode: "tabletop" | "floor";
  preferredKeywords: string[];
  avoidKeywords: string[];
  heightNorm: number;
  reason: string;
}

interface SceneVibePromptContext {
  vibeLabel: string;
  vibeDescription: string;
  sceneLabel: string;
  sceneContext: string;
}

interface SceneObjectInstance extends SceneObjectState {
  instanceId: string;
  defId: string;
}

interface SceneObjectOverlay {
  id: string;
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Cast a ray from screen pixel (sx, sy) and intersect with a horizontal
 *  plane at the given world-space height. Returns the hit point or null. */
function unprojectToPlane(
  sx: number,
  sy: number,
  cam: Camera3D,
  planeY: number
): Vec3 | null {
  const { forward, right, up, eye } = cameraBasis(cam);
  const focalLen = CANVAS_PX / 2 / Math.tan(((cam.fov / 2) * Math.PI) / 180);
  const nx = (sx - CANVAS_PX / 2) / focalLen;
  const ny = -(sy - CANVAS_PX / 2) / focalLen;
  const dir = v3Norm(
    v3Add(v3Add(forward, v3Scale(right, nx)), v3Scale(up, ny))
  );
  if (Math.abs(dir[1]) < 1e-6) return null; // ray parallel to plane
  const t = (planeY - eye[1]) / dir[1];
  if (t < 0) return null; // intersection behind camera
  return v3Add(eye, v3Scale(dir, t));
}

/** Cast a ray from screen pixel (sx, sy) and intersect with a vertical
 *  plane at the given world-space z depth. Returns the hit point or null. */
function unprojectToZPlane(
  sx: number,
  sy: number,
  cam: Camera3D,
  planeZ: number
): Vec3 | null {
  const { forward, right, up, eye } = cameraBasis(cam);
  const focalLen = CANVAS_PX / 2 / Math.tan(((cam.fov / 2) * Math.PI) / 180);
  const nx = (sx - CANVAS_PX / 2) / focalLen;
  const ny = -(sy - CANVAS_PX / 2) / focalLen;
  const dir = v3Norm(
    v3Add(v3Add(forward, v3Scale(right, nx)), v3Scale(up, ny))
  );
  if (Math.abs(dir[2]) < 1e-6) return null;
  const t = (planeZ - eye[2]) / dir[2];
  if (t < 0) return null;
  return v3Add(eye, v3Scale(dir, t));
}

/** Cast a ray from screen pixel (sx, sy) and intersect with a vertical
 *  plane at the given world-space x depth. Returns the hit point or null. */
function unprojectToXPlane(
  sx: number,
  sy: number,
  cam: Camera3D,
  planeX: number
): Vec3 | null {
  const { forward, right, up, eye } = cameraBasis(cam);
  const focalLen = CANVAS_PX / 2 / Math.tan(((cam.fov / 2) * Math.PI) / 180);
  const nx = (sx - CANVAS_PX / 2) / focalLen;
  const ny = -(sy - CANVAS_PX / 2) / focalLen;
  const dir = v3Norm(
    v3Add(v3Add(forward, v3Scale(right, nx)), v3Scale(up, ny))
  );
  if (Math.abs(dir[0]) < 1e-6) return null;
  const t = (planeX - eye[0]) / dir[0];
  if (t < 0) return null;
  return v3Add(eye, v3Scale(dir, t));
}

/** Cast a ray and snap to the highest surface whose AABB contains the hit.
 *  Falls back to the floor (y=0, clamped to room bounds). */
function unprojectToSurfaces(
  sx: number,
  sy: number,
  cam: Camera3D,
  surfaces: SnapSurface[],
  room: RoomDimensions
): { x: number; y: number; z: number; label: string; id?: string } {
  // Test surfaces highest-first so we snap to the top-most visible surface
  const sorted = [...surfaces].sort((a, b) => b.y - a.y);
  for (const surf of sorted) {
    const hit = unprojectToPlane(sx, sy, cam, surf.y);
    if (!hit) continue;
    if (
      hit[0] >= surf.x0 &&
      hit[0] <= surf.x1 &&
      hit[2] >= surf.z0 &&
      hit[2] <= surf.z1
    ) {
      return {
        x: hit[0],
        y: surf.y,
        z: hit[2],
        label: surf.label,
        id: surf.id,
      };
    }
  }
  // Floor fallback
  const floor = unprojectToPlane(sx, sy, cam, 0);
  return {
    x: floor ? Math.max(0, Math.min(room.width, floor[0])) : room.width / 2,
    y: 0,
    z: floor ? Math.max(0, Math.min(room.depth, floor[2])) : room.depth / 2,
    label: "floor",
    id: undefined,
  };
}

function projectSceneBoxOverlay(
  box: SceneBox,
  cam: Camera3D
): SceneObjectOverlay | null {
  if (!box.id) return null;
  const [ox, oy, oz] = box.origin;
  const [sx, sy, sz] = box.size;
  const corners: Vec3[] = [
    [ox, oy, oz],
    [ox + sx, oy, oz],
    [ox + sx, oy, oz + sz],
    [ox, oy, oz + sz],
    [ox, oy + sy, oz],
    [ox + sx, oy + sy, oz],
    [ox + sx, oy + sy, oz + sz],
    [ox, oy + sy, oz + sz],
  ];
  const projected = corners
    .map((corner) => project(corner, cam))
    .filter((p): p is [number, number] => Boolean(p));
  if (projected.length === 0) return null;
  const xs = projected.map((p) => p[0]);
  const ys = projected.map((p) => p[1]);
  const minX = Math.max(0, Math.min(...xs));
  const maxX = Math.min(CANVAS_PX, Math.max(...xs));
  const minY = Math.max(0, Math.min(...ys));
  const maxY = Math.min(CANVAS_PX, Math.max(...ys));
  const width = maxX - minX;
  const height = maxY - minY;
  if (width < 8 || height < 8) return null;
  return {
    id: box.id,
    label: box.label,
    left: minX / CANVAS_PX,
    top: minY / CANVAS_PX,
    width: width / CANVAS_PX,
    height: height / CANVAS_PX,
  };
}

function projectProductOverlay(
  pos: ProductPosition3D,
  productWidthCm: number,
  productHeightCm: number,
  cam: Camera3D
): SceneObjectOverlay | null {
  const pxPerCm = cmToPixels([pos.x, pos.y + productHeightCm / 2, pos.z], cam);
  if (pxPerCm <= 0) return null;
  const baseCenter = project([pos.x, pos.y, pos.z], cam);
  if (!baseCenter) return null;
  const widthPx = productWidthCm * pxPerCm;
  const heightPx = productHeightCm * pxPerCm;
  if (widthPx < 8 || heightPx < 8) return null;
  return {
    id: "product",
    label: "product",
    left: (baseCenter[0] - widthPx / 2) / CANVAS_PX,
    top: (baseCenter[1] - heightPx) / CANVAS_PX,
    width: widthPx / CANVAS_PX,
    height: heightPx / CANVAS_PX,
  };
}

// ---------------------------------------------------------------------------
// Room styles — 3D furniture definitions
// ---------------------------------------------------------------------------

type RoomStyleId = "custom-room" | "empty-room";

type EmptyRoomAestheticId =
  "scandinavian" | "boho" | "japandi" | "modern-classic" | "rustic-farmhouse";

interface RoomStyle {
  id: RoomStyleId | EmptyRoomAestheticId;
  label: string;
  drawFurniture: (ctx: CanvasRenderingContext2D, cam: Camera3D) => void;
  /** Top faces of major furniture pieces — used for product snap-to-surface */
  surfaces: SnapSurface[];
}

// --- Scandinavian ---
function drawScandinavianFurniture(
  ctx: CanvasRenderingContext2D,
  cam: Camera3D
) {
  // Sofa (left side, against back wall)
  ctx.strokeStyle = "#5a5a5a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [20, 0, 30], [160, 40, 80], cam); // base
  drawBox3D(ctx, [20, 40, 30], [160, 35, 20], cam); // back

  // Coffee table (center)
  ctx.strokeStyle = "#8a7a5a";
  drawBox3D(ctx, [140, 0, 150], [120, 45, 70], cam);

  // Side table (right side)
  ctx.strokeStyle = "#6a5a3a";
  drawBox3D(ctx, [300, 0, 40], [70, 65, 50], cam);

  // Bookshelf on back wall
  ctx.strokeStyle = "#7a7a7a";
  drawBox3D(ctx, [20, 75, 5], [80, 120, 30], cam);
  // shelves
  for (const sh of [100, 130, 160]) {
    drawLine3D(ctx, [20, sh, 5], [100, sh, 5], cam);
    drawLine3D(ctx, [20, sh, 35], [100, sh, 35], cam);
  }

  // Window on back wall
  fillFace3D(
    ctx,
    [160, 100, 2],
    [280, 100, 2],
    [280, 200, 2],
    [160, 200, 2],
    cam,
    "#d5e8f2"
  );
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1.4;
  drawLine3D(ctx, [160, 100, 2], [280, 100, 2], cam);
  drawLine3D(ctx, [160, 200, 2], [280, 200, 2], cam);
  drawLine3D(ctx, [160, 100, 2], [160, 200, 2], cam);
  drawLine3D(ctx, [280, 100, 2], [280, 200, 2], cam);
  // Inner frame (double rim)
  ctx.lineWidth = 0.8;
  drawLine3D(ctx, [167, 107, 2], [273, 107, 2], cam);
  drawLine3D(ctx, [167, 193, 2], [273, 193, 2], cam);
  drawLine3D(ctx, [167, 107, 2], [167, 193, 2], cam);
  drawLine3D(ctx, [273, 107, 2], [273, 193, 2], cam);
  ctx.lineWidth = 1;
  drawLine3D(ctx, [220, 107, 2], [220, 193, 2], cam);
  drawLine3D(ctx, [167, 150, 2], [273, 150, 2], cam);
}

// --- Boho ---
function drawBohoFurniture(ctx: CanvasRenderingContext2D, cam: Camera3D) {
  // Low floor cushion / daybed (left)
  ctx.strokeStyle = "#8a6a4a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [20, 0, 60], [140, 25, 90], cam);

  // Round wicker coffee table (center) — approximate as low box
  ctx.strokeStyle = "#a08a5a";
  drawBox3D(ctx, [170, 0, 160], [80, 35, 80], cam);

  // Macramé / tall plant stand (right rear)
  ctx.strokeStyle = "#6a8a5a";
  drawBox3D(ctx, [320, 0, 20], [30, 150, 30], cam);

  // Pouf (right, floor)
  ctx.strokeStyle = "#9a7a5a";
  drawBox3D(ctx, [300, 0, 200], [50, 30, 50], cam);

  // Arched mirror on back wall
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1;
  const mirrorCx = 200;
  const mirrorW = 50;
  drawLine3D(
    ctx,
    [mirrorCx - mirrorW, 80, 2],
    [mirrorCx - mirrorW, 190, 2],
    cam
  );
  drawLine3D(
    ctx,
    [mirrorCx + mirrorW, 80, 2],
    [mirrorCx + mirrorW, 190, 2],
    cam
  );
  drawLine3D(
    ctx,
    [mirrorCx - mirrorW, 80, 2],
    [mirrorCx + mirrorW, 80, 2],
    cam
  );
  // Arch top
  const archSegs = 8;
  for (let i = 0; i < archSegs; i++) {
    const a1 = Math.PI + (i / archSegs) * Math.PI;
    const a2 = Math.PI + ((i + 1) / archSegs) * Math.PI;
    drawLine3D(
      ctx,
      [mirrorCx + mirrorW * Math.cos(a1), 190 + mirrorW * Math.sin(a1), 2],
      [mirrorCx + mirrorW * Math.cos(a2), 190 + mirrorW * Math.sin(a2), 2],
      cam
    );
  }

  // Layered rug on floor (two rectangles)
  ctx.strokeStyle = "#b5a07a";
  ctx.lineWidth = 1;
  drawLine3D(ctx, [80, 0.5, 100], [320, 0.5, 100], cam);
  drawLine3D(ctx, [320, 0.5, 100], [320, 0.5, 300], cam);
  drawLine3D(ctx, [320, 0.5, 300], [80, 0.5, 300], cam);
  drawLine3D(ctx, [80, 0.5, 300], [80, 0.5, 100], cam);
}

// --- Japandi ---
function drawJapandiFurniture(ctx: CanvasRenderingContext2D, cam: Camera3D) {
  // Low platform bed / bench (left)
  ctx.strokeStyle = "#8a7a5a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [20, 0, 40], [160, 30, 100], cam);

  // Low coffee table
  ctx.strokeStyle = "#6a5a3a";
  drawBox3D(ctx, [200, 0, 160], [100, 30, 60], cam);

  // Shoji screen divider (right side)
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1;
  // Three panels
  for (let i = 0; i < 3; i++) {
    const x = 320 + i * 25;
    drawLine3D(ctx, [x, 0, 120], [x, 180, 120], cam);
    drawLine3D(ctx, [x, 0, 120], [x, 0, 122], cam);
    drawLine3D(ctx, [x, 180, 120], [x, 180, 122], cam);
    // Horizontal bars
    for (const hy of [45, 90, 135]) {
      drawLine3D(ctx, [x, hy, 120], [x + 25, hy, 120], cam);
    }
  }
  drawLine3D(ctx, [320 + 75, 0, 120], [320 + 75, 180, 120], cam);

  // Floor vase (right rear corner)
  ctx.strokeStyle = "#5a5a5a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [350, 0, 15], [20, 80, 20], cam);

  // Minimal window
  fillFace3D(
    ctx,
    [140, 100, 2],
    [260, 100, 2],
    [260, 210, 2],
    [140, 210, 2],
    cam,
    "#d5e8f2"
  );
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1.4;
  drawLine3D(ctx, [140, 100, 2], [260, 100, 2], cam);
  drawLine3D(ctx, [140, 210, 2], [260, 210, 2], cam);
  drawLine3D(ctx, [140, 100, 2], [140, 210, 2], cam);
  drawLine3D(ctx, [260, 100, 2], [260, 210, 2], cam);
  // Inner frame (double rim)
  ctx.lineWidth = 0.8;
  drawLine3D(ctx, [147, 107, 2], [253, 107, 2], cam);
  drawLine3D(ctx, [147, 203, 2], [253, 203, 2], cam);
  drawLine3D(ctx, [147, 107, 2], [147, 203, 2], cam);
  drawLine3D(ctx, [253, 107, 2], [253, 203, 2], cam);

  // Enso circle on wall (approximated as polygon)
  ctx.strokeStyle = "#bbb";
  ctx.lineWidth = 1;
  const ecx = 340,
    ecy = 160,
    er = 30;
  const segs = 12;
  for (let i = 0; i < segs - 2; i++) {
    const a1 = (i / segs) * Math.PI * 2;
    const a2 = ((i + 1) / segs) * Math.PI * 2;
    drawLine3D(
      ctx,
      [ecx + er * Math.cos(a1), ecy + er * Math.sin(a1), 2],
      [ecx + er * Math.cos(a2), ecy + er * Math.sin(a2), 2],
      cam
    );
  }
}

// --- Modern Classic ---
function drawModernClassicFurniture(
  ctx: CanvasRenderingContext2D,
  cam: Camera3D
) {
  // Tufted armchair (left front)
  ctx.strokeStyle = "#5a5a5a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [30, 0, 200], [90, 45, 80], cam); // seat
  drawBox3D(ctx, [30, 45, 200], [90, 50, 15], cam); // back

  // Console table (right, tall)
  ctx.strokeStyle = "#6a5a3a";
  drawBox3D(ctx, [290, 0, 30], [90, 80, 40], cam);

  // Table lamp on console (small box)
  ctx.strokeStyle = "#888";
  drawBox3D(ctx, [320, 80, 40], [30, 40, 20], cam);

  // Tall curtained window
  fillFace3D(
    ctx,
    [150, 80, 2],
    [270, 80, 2],
    [270, 230, 2],
    [150, 230, 2],
    cam,
    "#d5e8f2"
  );
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1.4;
  drawLine3D(ctx, [150, 80, 2], [270, 80, 2], cam);
  drawLine3D(ctx, [150, 230, 2], [270, 230, 2], cam);
  drawLine3D(ctx, [150, 80, 2], [150, 230, 2], cam);
  drawLine3D(ctx, [270, 80, 2], [270, 230, 2], cam);
  // Inner frame (double rim)
  ctx.lineWidth = 0.8;
  drawLine3D(ctx, [157, 87, 2], [263, 87, 2], cam);
  drawLine3D(ctx, [157, 223, 2], [263, 223, 2], cam);
  drawLine3D(ctx, [157, 87, 2], [157, 223, 2], cam);
  drawLine3D(ctx, [263, 87, 2], [263, 223, 2], cam);
  // Panes (on inner frame)
  ctx.lineWidth = 1;
  drawLine3D(ctx, [210, 87, 2], [210, 223, 2], cam);
  drawLine3D(ctx, [157, 130, 2], [263, 130, 2], cam);
  drawLine3D(ctx, [157, 180, 2], [263, 180, 2], cam);
  // Curtain lines
  ctx.strokeStyle = "#aaa";
  for (const cx of [135, 140, 145, 275, 280, 285]) {
    drawLine3D(ctx, [cx, 75, 2], [cx, 235, 2], cam);
  }

  // Crown molding (line along back wall top)
  ctx.strokeStyle = "#999";
  ctx.lineWidth = 1;
  drawLine3D(ctx, [0, ROOM_H - 10, 2], [ROOM_W, ROOM_H - 10, 2], cam);
  drawLine3D(ctx, [0, ROOM_H - 15, 2], [ROOM_W, ROOM_H - 15, 2], cam);

  // Herringbone floor pattern (subtle lines on floor)
  ctx.strokeStyle = "#d8d0c0";
  ctx.lineWidth = 0.5;
  for (let x = 0; x < ROOM_W; x += 60) {
    for (let z = 0; z < ROOM_D; z += 60) {
      drawLine3D(ctx, [x, 0.3, z], [x + 30, 0.3, z + 15], cam);
      drawLine3D(ctx, [x + 30, 0.3, z], [x + 60, 0.3, z + 15], cam);
    }
  }
}

// --- Rustic Farmhouse ---
function drawRusticFarmhouseFurniture(
  ctx: CanvasRenderingContext2D,
  cam: Camera3D
) {
  // Farmhouse dining table (center)
  ctx.strokeStyle = "#6a5a3a";
  ctx.lineWidth = 2;
  drawBox3D(ctx, [120, 0, 120], [160, 75, 80], cam);
  // Cross brace under table
  ctx.lineWidth = 1;
  drawLine3D(ctx, [130, 35, 160], [270, 35, 160], cam);

  // Bench (left side of table)
  ctx.strokeStyle = "#7a6a4a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [130, 0, 210], [140, 45, 30], cam);

  // Rustic cabinet (right rear)
  ctx.strokeStyle = "#5a4a2a";
  ctx.lineWidth = 1.5;
  drawBox3D(ctx, [310, 0, 10], [80, 120, 45], cam);
  // Cabinet door divider
  drawLine3D(ctx, [350, 0, 10], [350, 120, 10], cam);

  // Exposed ceiling beams
  ctx.strokeStyle = "#8a7050";
  ctx.lineWidth = 2;
  for (const bz of [80, 200, 320]) {
    drawLine3D(ctx, [0, ROOM_H - 5, bz], [ROOM_W, ROOM_H - 5, bz], cam);
    drawLine3D(ctx, [0, ROOM_H - 15, bz], [ROOM_W, ROOM_H - 15, bz], cam);
  }

  // Barn-style window
  fillFace3D(
    ctx,
    [140, 120, 2],
    [260, 120, 2],
    [260, 220, 2],
    [140, 220, 2],
    cam,
    "#d5e8f2"
  );
  ctx.strokeStyle = "#888";
  ctx.lineWidth = 1.4;
  drawLine3D(ctx, [140, 120, 2], [260, 120, 2], cam);
  drawLine3D(ctx, [140, 220, 2], [260, 220, 2], cam);
  drawLine3D(ctx, [140, 120, 2], [140, 220, 2], cam);
  drawLine3D(ctx, [260, 120, 2], [260, 220, 2], cam);
  // Inner frame (double rim)
  ctx.lineWidth = 0.8;
  drawLine3D(ctx, [147, 127, 2], [253, 127, 2], cam);
  drawLine3D(ctx, [147, 213, 2], [253, 213, 2], cam);
  drawLine3D(ctx, [147, 127, 2], [147, 213, 2], cam);
  drawLine3D(ctx, [253, 127, 2], [253, 213, 2], cam);
  // Mullion + rail (on inner frame)
  ctx.lineWidth = 1;
  drawLine3D(ctx, [200, 127, 2], [200, 213, 2], cam);
  drawLine3D(ctx, [147, 170, 2], [253, 170, 2], cam);

  // Woven basket on floor (small box)
  ctx.strokeStyle = "#9a8a5a";
  ctx.lineWidth = 1;
  drawBox3D(ctx, [20, 0, 300], [35, 40, 35], cam);

  // Plank floor lines
  ctx.strokeStyle = "#c8b890";
  ctx.lineWidth = 0.5;
  for (let x = 0; x < ROOM_W; x += 50) {
    drawLine3D(ctx, [x, 0.3, 0], [x, 0.3, ROOM_D], cam);
  }
}

const ROOM_STYLES: RoomStyle[] = [
  {
    id: "empty-room",
    label: "Empty Room",
    drawFurniture: (ctx, cam) =>
      drawEmptyRoomFurniture(ctx, cam, {
        width: ROOM_W,
        height: ROOM_H,
        depth: ROOM_D,
      }),
    surfaces: [],
  },
];

const EMPTY_ROOM_AESTHETICS: Array<RoomStyle & { id: EmptyRoomAestheticId }> = [
  {
    id: "scandinavian",
    label: "Scandinavian",
    drawFurniture: drawScandinavianFurniture,
    // drawBox3D origin [ox,oy,oz] size [sx,sy,sz] → top y=oy+sy, x=[ox,ox+sx], z=[oz,oz+sz]
    surfaces: [
      { label: "sofa", y: 40, x0: 20, x1: 180, z0: 30, z1: 110 },
      { label: "coffee table", y: 45, x0: 140, x1: 260, z0: 150, z1: 220 },
      { label: "side table", y: 65, x0: 300, x1: 370, z0: 40, z1: 90 },
    ],
  },
  {
    id: "boho",
    label: "Boho",
    drawFurniture: drawBohoFurniture,
    surfaces: [
      { label: "daybed", y: 25, x0: 20, x1: 160, z0: 60, z1: 150 },
      { label: "coffee table", y: 35, x0: 170, x1: 250, z0: 160, z1: 240 },
      { label: "pouf", y: 30, x0: 300, x1: 350, z0: 200, z1: 250 },
      { label: "plant stand", y: 150, x0: 320, x1: 350, z0: 20, z1: 50 },
    ],
  },
  {
    id: "japandi",
    label: "Japandi",
    drawFurniture: drawJapandiFurniture,
    surfaces: [
      { label: "bench", y: 30, x0: 20, x1: 180, z0: 40, z1: 140 },
      { label: "coffee table", y: 30, x0: 200, x1: 300, z0: 160, z1: 220 },
    ],
  },
  {
    id: "modern-classic",
    label: "Modern Classic",
    drawFurniture: drawModernClassicFurniture,
    surfaces: [
      { label: "armchair seat", y: 45, x0: 30, x1: 120, z0: 200, z1: 280 },
      { label: "console table", y: 80, x0: 290, x1: 380, z0: 30, z1: 70 },
      { label: "lamp shelf", y: 120, x0: 320, x1: 350, z0: 40, z1: 60 },
    ],
  },
  {
    id: "rustic-farmhouse",
    label: "Rustic Farmhouse",
    drawFurniture: drawRusticFarmhouseFurniture,
    surfaces: [
      { label: "dining table", y: 75, x0: 120, x1: 280, z0: 120, z1: 200 },
      { label: "bench", y: 45, x0: 130, x1: 270, z0: 210, z1: 240 },
      { label: "cabinet top", y: 120, x0: 310, x1: 390, z0: 10, z1: 55 },
    ],
  },
];

// ===========================================================================
// Empty Room — Interior Type + Style Prompt Builder
// ===========================================================================

interface InteriorTypeInfo {
  id: InteriorType;
  label: string;
}

interface FramingPreferenceInfo {
  id: FramingPreference;
  label: string;
  description: string;
}

const INTERIOR_TYPES: InteriorTypeInfo[] = [
  { id: "living-room", label: "Living Room" },
  { id: "bedroom", label: "Bedroom" },
  { id: "dining-room", label: "Dining Room" },
  { id: "home-office", label: "Home Office" },
  { id: "hallway", label: "Hallway" },
];

function mapBrandVibeToAesthetic(
  vibe?: Pick<Vibe, "label" | "description"> | null
): EmptyRoomAestheticId {
  const source = `${vibe?.label || ""} ${vibe?.description || ""}`.trim();
  if (!source) return "scandinavian";

  return pickBestMappedOption(
    source,
    [
      {
        id: "scandinavian",
        keywords: [
          "scandinavian",
          "nordic",
          "airy",
          "hygge",
          "light wood",
          "soft minimal",
          "clean minimal",
        ],
      },
      {
        id: "boho",
        keywords: [
          "boho",
          "bohemian",
          "eclectic",
          "woven",
          "rattan",
          "earthy",
          "artisan",
          "layered",
        ],
      },
      {
        id: "japandi",
        keywords: [
          "japandi",
          "wabi",
          "zen",
          "japanese",
          "serene",
          "calm",
          "quiet luxury",
          "minimal warm",
        ],
      },
      {
        id: "modern-classic",
        keywords: [
          "modern classic",
          "classic",
          "elegant",
          "luxury",
          "luxurious",
          "refined",
          "timeless",
          "tailored",
          "sophisticated",
        ],
      },
      {
        id: "rustic-farmhouse",
        keywords: [
          "rustic",
          "farmhouse",
          "country",
          "cottage",
          "weathered",
          "barn",
          "heritage",
          "organic",
          "lived-in",
        ],
      },
    ],
    "scandinavian"
  );
}

const FRAMING_PREFERENCES: FramingPreferenceInfo[] = [
  {
    id: "wide",
    label: "More room",
    description:
      "Keeps the product central but shows more of the surrounding room.",
  },
  {
    id: "balanced",
    label: "More product",
    description:
      "Makes the product the focal point while preserving room context.",
  },
];

const ROOM_PLACEMENT_PREFERENCES: Array<{
  id: RoomPlacementPreference;
  label: string;
}> = [
  { id: "center", label: "Center" },
  { id: "near-wall", label: "Near wall" },
];

const ROOM_VIEW_ANGLES: Array<{ id: RoomViewAngle; label: string }> = [
  { id: "frontal", label: "Frontal" },
  { id: "corner", label: "Corner" },
];

function rotate90(rotation: 0 | 90 | 180 | 270): 0 | 90 | 180 | 270 {
  return ((rotation + 90) % 360 || 0) as 0 | 90 | 180 | 270;
}

function placeSceneObjectFromScreen(
  def: SceneObjectDefinition,
  state: Pick<SceneObjectState, "rotation" | "wall">,
  sx: number,
  sy: number,
  cam: Camera3D,
  room: RoomDimensions
): Vec3 {
  const [boxW, boxH, boxD] = getSceneObjectSize(def, state);
  if (def.placement === "floor") {
    const floorHit = unprojectToPlane(sx, sy, cam, 0);
    return clampSceneObjectOrigin(
      def,
      state,
      [
        (floorHit?.[0] ?? room.width / 2) - boxW / 2,
        0,
        (floorHit?.[2] ?? room.depth / 2) - boxD / 2,
      ],
      room
    );
  }

  if (state.wall === "left") {
    const wallHit = unprojectToXPlane(sx, sy, cam, 8);
    return clampSceneObjectOrigin(
      def,
      state,
      [
        8,
        (wallHit?.[1] ?? room.height / 2) - boxH / 2,
        (wallHit?.[2] ?? room.depth / 2) - boxD / 2,
      ],
      room
    );
  }
  if (state.wall === "right") {
    const wallHit = unprojectToXPlane(sx, sy, cam, room.width - 8);
    return clampSceneObjectOrigin(
      def,
      state,
      [
        room.width - boxW - 8,
        (wallHit?.[1] ?? room.height / 2) - boxH / 2,
        (wallHit?.[2] ?? room.depth / 2) - boxD / 2,
      ],
      room
    );
  }

  const wallHit = unprojectToZPlane(sx, sy, cam, 8);
  return clampSceneObjectOrigin(
    def,
    state,
    [
      (wallHit?.[0] ?? room.width / 2) - boxW / 2,
      (wallHit?.[1] ?? room.height / 2) - boxH / 2,
      8,
    ],
    room
  );
}

function drawRoom3D(
  canvas: HTMLCanvasElement,
  cam: Camera3D,
  styleId: RoomStyleId,
  room: RoomDimensions,
  sceneBoxes: SceneBox[] = []
): string {
  const style = ROOM_STYLES.find((s) => s.id === styleId) ?? ROOM_STYLES[0];
  return drawRoom3DBase(canvas, cam, room, sceneBoxes, style.drawFurniture);
}

// ---------------------------------------------------------------------------
// Composite: place product into 3D room
// ---------------------------------------------------------------------------

function defaultProductPosForRoom(room: RoomDimensions): ProductPosition3D {
  return {
    x: room.width / 2,
    y: 0,
    z: room.depth * 0.45,
  };
}

async function compositeProduct3D(
  sketchDataUrl: string,
  productDataUrl: string,
  productHeightCm: number,
  productWidthCm: number,
  pos: ProductPosition3D,
  cam: Camera3D,
  annotations = true
): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_PX;
  canvas.height = CANVAS_PX;
  const ctx = canvas.getContext("2d")!;

  // Draw sketch background
  const sketchImg = await loadImage(sketchDataUrl);
  ctx.drawImage(sketchImg, 0, 0, CANVAS_PX, CANVAS_PX);

  // Compute perspective scale at mid-height of the product above its surface
  const pxPerCm = cmToPixels([pos.x, pos.y + productHeightCm / 2, pos.z], cam);
  const prodWidthPx = productWidthCm * pxPerCm;
  const prodHeightPx = productHeightCm * pxPerCm;

  // Project the product base center (bottom-center on its surface)
  const baseCenter = project([pos.x, pos.y, pos.z], cam);
  if (!baseCenter) return sketchDataUrl; // off-screen

  const prodX = baseCenter[0] - prodWidthPx / 2;
  const prodY = baseCenter[1] - prodHeightPx;

  // Draw product image
  const prodImg = await loadImage(productDataUrl);
  ctx.drawImage(prodImg, prodX, prodY, prodWidthPx, prodHeightPx);

  if (annotations) {
    // Draw dimension bounding box
    ctx.strokeStyle = "#e74c3c";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(prodX, prodY, prodWidthPx, prodHeightPx);
    ctx.setLineDash([]);

    // Dimension labels
    ctx.fillStyle = "#e74c3c";
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "left";
    ctx.fillText(
      `${productHeightCm} cm`,
      prodX + prodWidthPx + 6,
      prodY + prodHeightPx / 2
    );
    ctx.textAlign = "center";
    ctx.fillText(`${productWidthCm} cm`, baseCenter[0], prodY - 6);
  }

  // Ground shadow-sm hint
  ctx.fillStyle = "rgba(0,0,0,0.06)";
  ctx.beginPath();
  ctx.ellipse(
    baseCenter[0],
    baseCenter[1],
    prodWidthPx * 0.4,
    prodWidthPx * 0.08,
    0,
    0,
    Math.PI * 2
  );
  ctx.fill();

  return canvas.toDataURL("image/png");
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function normalizeRect(rect: Partial<NormalizedRect>): NormalizedRect {
  const left = clamp01(rect.left ?? 0);
  const top = clamp01(rect.top ?? 0);
  const width = Math.max(0.08, Math.min(1 - left, rect.width ?? 0.3));
  const height = Math.max(0.08, Math.min(1 - top, rect.height ?? 0.3));
  return { left, top, width, height };
}

function rectCenter(rect: NormalizedRect): { x: number; y: number } {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function getFramingPreferenceConfig(preference: FramingPreference): {
  cropMultiplier: number;
  minCrop: number;
  distanceMultiplier: number;
  fovDelta: number;
  subjectSentence: string;
  reasonSentence: string;
} {
  switch (preference) {
    case "wide":
      return {
        cropMultiplier: 2.2,
        minCrop: 0.52,
        distanceMultiplier: 0.95,
        fovDelta: -1,
        subjectSentence:
          "Use a wider shot so the product stays central while some room context remains visible around it.",
        reasonSentence:
          "The camera is pulled back slightly so the room reads around the product without dominating the frame.",
      };
    case "balanced":
    default:
      return {
        cropMultiplier: 1.75,
        minCrop: 0.42,
        distanceMultiplier: 0.8,
        fovDelta: -4,
        subjectSentence:
          "Use a balanced hero shot where the product is clearly the focal point and fills roughly half the frame.",
        reasonSentence:
          "The camera keeps the product central and prominent while preserving enough room context for a believable interior.",
      };
  }
}

function appendSentence(base: string, sentence: string): string {
  const trimmedBase = base.trim();
  const trimmedSentence = sentence.trim();
  if (!trimmedBase) return trimmedSentence;
  if (!trimmedSentence) return trimmedBase;
  return trimmedBase.endsWith(".")
    ? `${trimmedBase} ${trimmedSentence}`
    : `${trimmedBase}. ${trimmedSentence}`;
}

function fitCameraRectAroundProduct(
  placementRect: NormalizedRect,
  preferredRect?: NormalizedRect,
  framingPreference: FramingPreference = "balanced"
): NormalizedRect {
  const framing = getFramingPreferenceConfig(framingPreference);
  const center = rectCenter(placementRect);
  const rawWidth = placementRect.width * framing.cropMultiplier;
  const rawHeight = placementRect.height * framing.cropMultiplier;
  const preferredWidth = preferredRect
    ? rawWidth * 0.8 + preferredRect.width * 0.2
    : rawWidth;
  const preferredHeight = preferredRect
    ? rawHeight * 0.8 + preferredRect.height * 0.2
    : rawHeight;
  const width = Math.min(1, Math.max(preferredWidth, framing.minCrop));
  const height = Math.min(1, Math.max(preferredHeight, framing.minCrop));
  const left = clamp01(center.x - width / 2);
  const top = clamp01(center.y - height / 2);
  return normalizeRect({
    left: Math.min(left, 1 - width),
    top: Math.min(top, 1 - height),
    width,
    height,
  });
}

function rectToCropSpace(
  rect: NormalizedRect,
  cropRect: NormalizedRect
): NormalizedRect | null {
  const left = (rect.left - cropRect.left) / cropRect.width;
  const top = (rect.top - cropRect.top) / cropRect.height;
  const right = (rect.left + rect.width - cropRect.left) / cropRect.width;
  const bottom = (rect.top + rect.height - cropRect.top) / cropRect.height;
  const clampedLeft = clamp01(left);
  const clampedTop = clamp01(top);
  const clampedRight = clamp01(right);
  const clampedBottom = clamp01(bottom);
  if (clampedRight - clampedLeft < 0.04 || clampedBottom - clampedTop < 0.04) {
    return null;
  }
  return {
    left: clampedLeft,
    top: clampedTop,
    width: clampedRight - clampedLeft,
    height: clampedBottom - clampedTop,
  };
}

function fitRectPreservingAspect(
  rect: NormalizedRect,
  aspectRatio: number
): NormalizedRect {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return rect;
  const boxRatio = rect.width / rect.height;
  if (Math.abs(boxRatio - aspectRatio) < 0.0001) return rect;

  if (aspectRatio >= boxRatio) {
    const fittedHeight = rect.width / aspectRatio;
    return {
      left: rect.left,
      top: rect.top + (rect.height - fittedHeight),
      width: rect.width,
      height: fittedHeight,
    };
  }

  const fittedWidth = rect.height * aspectRatio;
  return {
    left: rect.left + (rect.width - fittedWidth) / 2,
    top: rect.top,
    width: fittedWidth,
    height: rect.height,
  };
}

function inferProductPlacementProfile(params: {
  category?: string | null;
  description?: string | null;
  productHeightCm: number;
  roomHeightCm: number;
}): ProductPlacementProfile {
  const text =
    `${params.category || ""} ${params.description || ""}`.toLowerCase();
  const relativeHeight =
    params.productHeightCm / Math.max(1, params.roomHeightCm);

  const tabletopKeywords = [
    "plant",
    "vase",
    "decor",
    "decoration",
    "trinket",
    "ceramic",
    "candle",
    "lamp",
    "table lamp",
    "bottle",
    "diffuser",
    "frame",
    "sculpture",
    "bowl",
  ];
  const floorKeywords = [
    "chair",
    "stool",
    "ottoman",
    "bench",
    "shelf",
    "cabinet",
    "sofa",
    "table",
    "floor lamp",
    "mirror",
    "dresser",
    "sideboard",
    "plinth",
  ];

  if (
    tabletopKeywords.some((keyword) => text.includes(keyword)) ||
    relativeHeight < 0.22
  ) {
    return {
      mode: "tabletop",
      preferredKeywords: [
        "shelf",
        "sideboard",
        "console",
        "nightstand",
        "coffee table",
        "table",
        "plinth",
        "cabinet",
      ],
      avoidKeywords: ["floor", "ground", "rug", "walkway"],
      heightNorm: Math.max(0.08, Math.min(0.22, relativeHeight * 1.2)),
      reason:
        "Small or decorative products should be staged on elevated support surfaces whenever available.",
    };
  }

  if (
    floorKeywords.some((keyword) => text.includes(keyword)) ||
    relativeHeight > 0.33
  ) {
    return {
      mode: "floor",
      preferredKeywords: ["floor", "plinth", "bench", "low table"],
      avoidKeywords: ["shelf", "wall"],
      heightNorm: Math.max(0.18, Math.min(0.55, relativeHeight * 1.05)),
      reason:
        "Larger or furniture-scale products should anchor on the floor or a substantial low support.",
    };
  }

  return {
    mode: "tabletop",
    preferredKeywords: ["sideboard", "console", "shelf", "table", "plinth"],
    avoidKeywords: ["floor", "ground"],
    heightNorm: Math.max(0.12, Math.min(0.28, relativeHeight * 1.1)),
    reason:
      "Mid-sized products are safer on stable display surfaces unless they are clearly floor-standing.",
  };
}

function fallbackFloorSurface(): CustomRoomSurface {
  return {
    id: "fallback-floor",
    label: "floor",
    surfaceType: "floor",
    left: 0.18,
    top: 0.72,
    width: 0.64,
    height: 0.2,
  };
}

function chooseSurfaceForProduct(
  surfaces: CustomRoomSurface[],
  profile: ProductPlacementProfile
): CustomRoomSurface {
  const candidates = surfaces.length > 0 ? surfaces : [fallbackFloorSurface()];
  const scored = candidates.map((surface) => {
    const text = `${surface.label} ${surface.surfaceType}`.toLowerCase();
    let score = 0;
    for (const keyword of profile.preferredKeywords) {
      if (text.includes(keyword)) score += 4;
    }
    for (const keyword of profile.avoidKeywords) {
      if (text.includes(keyword)) score -= 5;
    }
    if (profile.mode === "tabletop") {
      score += surface.top < 0.75 ? 2 : -2;
      score += surface.width > 0.08 ? 1 : -1;
    } else {
      score += text.includes("floor") ? 6 : 0;
      score += surface.top > 0.58 ? 2 : -1;
      score += surface.width > 0.16 ? 1 : 0;
    }
    return { surface, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.surface ?? fallbackFloorSurface();
}

function derivePlacementRectFromSurface(params: {
  surface: CustomRoomSurface;
  profile: ProductPlacementProfile;
  imageAspectRatio: number;
}): NormalizedRect {
  const { surface, profile, imageAspectRatio } = params;
  const targetHeight =
    profile.mode === "floor" ? profile.heightNorm : profile.heightNorm;
  const targetWidth = targetHeight * Math.max(0.35, imageAspectRatio);
  const maxSurfaceWidth = Math.max(0.08, surface.width * 0.72);
  const fittedWidth = Math.min(targetWidth, maxSurfaceWidth);
  const fittedHeight = fittedWidth / Math.max(0.2, imageAspectRatio);

  const centerX = surface.left + surface.width / 2;
  const baseY =
    profile.mode === "floor"
      ? surface.top + surface.height * 0.96
      : surface.top + surface.height * 0.62;

  return normalizeRect({
    left: centerX - fittedWidth / 2,
    top: baseY - fittedHeight,
    width: fittedWidth,
    height: fittedHeight,
  });
}

function buildDeterministicPlacement(params: {
  plan: CustomRoomPlan;
  productAnalysis?: { category: string; description: string } | null;
  productHeightCm: number;
  imageAspectRatio: number;
}): {
  surface: CustomRoomSurface;
  rect: NormalizedRect;
  reason: string;
} {
  const profile = inferProductPlacementProfile({
    category: params.productAnalysis?.category,
    description: params.productAnalysis?.description,
    productHeightCm: params.productHeightCm,
    roomHeightCm: params.plan.estimatedRoomDimensionsCm.height,
  });
  const surface = chooseSurfaceForProduct(params.plan.surfaces, profile);
  const rect = derivePlacementRectFromSurface({
    surface,
    profile,
    imageAspectRatio: params.imageAspectRatio,
  });
  return {
    surface,
    rect,
    reason: `${profile.reason} Selected ${surface.label} as the best matching support surface.`,
  };
}

function cropSpacePointToRoomSpace(
  fracX: number,
  fracY: number,
  cropRect: NormalizedRect
): { x: number; y: number } {
  return {
    x: cropRect.left + clamp01(fracX) * cropRect.width,
    y: cropRect.top + clamp01(fracY) * cropRect.height,
  };
}

function normalizeCustomRoomPlan(raw: {
  styleLabel?: string;
  roomType?: string;
  roomContext?: string;
  lineArtPrompt?: string;
  estimatedRoomDimensionsCm?: {
    width?: number;
    height?: number;
    depth?: number;
    confidence?: string;
    reasoning?: string;
  };
  placement?: {
    surfaceType?: string;
    surfaceLabel?: string;
    reason?: string;
    estimatedProductScaleNote?: string;
    left?: number;
    top?: number;
    width?: number;
    height?: number;
  };
  camera?: {
    left?: number;
    top?: number;
    width?: number;
    height?: number;
    reason?: string;
    subjectFraming?: string;
  };
  surfaces?: Array<{
    id?: string;
    label?: string;
    surfaceType?: string;
    left?: number;
    top?: number;
    width?: number;
    height?: number;
  }>;
}): CustomRoomPlan {
  const placementRect = normalizeRect(raw.placement ?? {});
  const cameraRect = fitCameraRectAroundProduct(
    placementRect,
    raw.camera ? normalizeRect(raw.camera) : undefined
  );
  return {
    styleLabel: raw.styleLabel?.trim() || "Custom Room",
    roomType: raw.roomType?.trim() || "interior",
    roomContext: raw.roomContext?.trim() || "",
    lineArtPrompt:
      raw.lineArtPrompt?.trim() ||
      "Convert this room into clean architectural line art for a product placement preview.",
    estimatedRoomDimensionsCm: {
      width: Math.max(200, Number(raw.estimatedRoomDimensionsCm?.width) || 400),
      height: Math.max(
        200,
        Number(raw.estimatedRoomDimensionsCm?.height) || DEFAULT_ROOM_H
      ),
      depth: Math.max(200, Number(raw.estimatedRoomDimensionsCm?.depth) || 400),
      confidence: raw.estimatedRoomDimensionsCm?.confidence?.trim() || "medium",
      reasoning:
        raw.estimatedRoomDimensionsCm?.reasoning?.trim() ||
        "Estimated from perspective, furniture proportions, and architectural cues.",
    },
    placement: {
      surfaceType: raw.placement?.surfaceType?.trim() || "surface",
      surfaceLabel: raw.placement?.surfaceLabel?.trim() || "display surface",
      reason:
        raw.placement?.reason?.trim() ||
        "Chosen as the most stable focal surface.",
      estimatedProductScaleNote:
        raw.placement?.estimatedProductScaleNote?.trim() ||
        "Placement size inferred from the supplied product dimensions relative to the room.",
      rect: placementRect,
    },
    camera: {
      rect: cameraRect,
      reason:
        raw.camera?.reason?.trim() ||
        "Crop keeps the product centered while preserving enough room context.",
      subjectFraming:
        raw.camera?.subjectFraming?.trim() ||
        "Centered hero product framing with visible surrounding room context.",
    },
    surfaces:
      raw.surfaces?.map((surface, index) => ({
        id: surface.id?.trim() || `surface-${index + 1}`,
        label: surface.label?.trim() || `surface ${index + 1}`,
        surfaceType: surface.surfaceType?.trim() || "surface",
        ...normalizeRect(surface),
      })) ?? [],
  };
}

async function readFileAsDataUrl(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function ensureDataUrl(src: string): Promise<string> {
  if (src.startsWith("data:")) return src;
  const response = await fetch(src);
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadTextFile(
  content: string,
  filename: string,
  mimeType = "text/plain;charset=utf-8"
) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadJsonFile(payload: unknown, filename: string) {
  downloadTextFile(
    JSON.stringify(payload, null, 2),
    filename,
    "application/json;charset=utf-8"
  );
}

async function compositeProductOnCustomRoom(
  lineArtDataUrl: string,
  productDataUrl: string,
  placementRect: NormalizedRect,
  cropRect: NormalizedRect,
  imageAspectRatio: number,
  annotations = true
): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_PX;
  canvas.height = CANVAS_PX;
  const ctx = canvas.getContext("2d")!;

  const roomImg = await loadImage(lineArtDataUrl);
  const sx = cropRect.left * roomImg.width;
  const sy = cropRect.top * roomImg.height;
  const sw = cropRect.width * roomImg.width;
  const sh = cropRect.height * roomImg.height;
  ctx.drawImage(roomImg, sx, sy, sw, sh, 0, 0, CANVAS_PX, CANVAS_PX);

  const rawDisplayRect =
    rectToCropSpace(placementRect, cropRect) ??
    normalizeRect({ left: 0.35, top: 0.35, width: 0.3, height: 0.3 });
  const displayRect = fitRectPreservingAspect(rawDisplayRect, imageAspectRatio);
  const prodX = displayRect.left * CANVAS_PX;
  const prodY = displayRect.top * CANVAS_PX;
  const prodW = displayRect.width * CANVAS_PX;
  const prodH = displayRect.height * CANVAS_PX;

  const prodImg = await loadImage(productDataUrl);
  ctx.fillStyle = "rgba(0,0,0,0.08)";
  ctx.beginPath();
  ctx.ellipse(
    prodX + prodW / 2,
    prodY + prodH * 0.98,
    prodW * 0.34,
    Math.max(8, prodH * 0.05),
    0,
    0,
    Math.PI * 2
  );
  ctx.fill();
  ctx.drawImage(prodImg, prodX, prodY, prodW, prodH);

  if (annotations) {
    ctx.strokeStyle = "#e74c3c";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(prodX, prodY, prodW, prodH);
    ctx.setLineDash([]);
  }

  return canvas.toDataURL("image/png");
}

async function analyzeCustomRoomStyle(
  roomDataUrl: string,
  productDataUrl: string | null,
  params: {
    productCategory: string;
    productDescription: string;
    productHeightCm: number;
    productWidthCm: number;
  }
): Promise<CustomRoomPlan> {
  const roomPath = await uploadDataUrlToGCS(
    roomDataUrl,
    buildTempPath("custom_room")
  );
  const productPath = productDataUrl
    ? await uploadDataUrlToGCS(
        productDataUrl,
        buildTempPath("custom_room_product")
      )
    : undefined;
  const result = await callAnalysis<{
    styleLabel?: string;
    roomType?: string;
    roomContext?: string;
    lineArtPrompt?: string;
    estimatedRoomDimensionsCm?: {
      width?: number;
      height?: number;
      depth?: number;
      confidence?: string;
      reasoning?: string;
    };
    placement?: {
      surfaceType?: string;
      surfaceLabel?: string;
      reason?: string;
      estimatedProductScaleNote?: string;
      left?: number;
      top?: number;
      width?: number;
      height?: number;
    };
    camera?: {
      left?: number;
      top?: number;
      width?: number;
      height?: number;
      reason?: string;
      subjectFraming?: string;
    };
    surfaces?: Array<{
      id?: string;
      label?: string;
      surfaceType?: string;
      left?: number;
      top?: number;
      width?: number;
      height?: number;
    }>;
  }>(
    "analyze_custom_room_style",
    {
      room: roomPath,
      ...(productPath ? { product: productPath } : {}),
    },
    params
  );

  return normalizeCustomRoomPlan(result);
}

async function generateCustomRoomLineArt(
  roomDataUrl: string,
  plan: CustomRoomPlan,
  productAnalysis?: { category: string; description: string } | null,
  subjectFraming?: string
): Promise<{ imageUrl: string | null; prompt: string }> {
  const generationContext = snapshotGenerationContext();
  const roomPath = await uploadDataUrlToGCS(
    roomDataUrl,
    buildTempPath("line_art_room")
  );
  const replacementNote = productAnalysis
    ? `This room will receive a replacement product of type "${productAnalysis.category}" (${productAnalysis.description}). If the reference room already contains a matching or similar product/object in the intended placement area, remove it from the line-art preview so the new product can replace it cleanly.`
    : "If the reference room already contains a product in the intended placement area, remove it from the line-art preview so a new product can replace it cleanly.";
  const prompt = `Image 1 is a real room reference.

Task: Convert this room into a clean monochrome architectural line-art preview for product placement planning.

Requirements:
- Preserve the room architecture, major furniture silhouettes, key surfaces, and overall perspective from the reference.
- Remove photorealistic textures, color, clutter, and soft decoration details.
- Use crisp charcoal-gray line work on a warm off-white background.
- Keep important staging surfaces readable, especially ${plan.placement.surfaceLabel}.
- Do not add a product.
- ${replacementNote}
- Keep the framing suitable for a centered hero product crop.

Scene context:
- style: ${plan.styleLabel}
- room type: ${plan.roomType}
- room context: ${plan.roomContext}
- suggested framing: ${plan.camera.subjectFraming}
 - suggested framing: ${subjectFraming || plan.camera.subjectFraming}

Extra guidance:
${plan.lineArtPrompt}`;

  const result = await callGenerateImage(
    "free_prompt",
    { input: roomPath },
    {
      prompt,
      rawPrompt: true,
      aspectRatio: "1:1",
      temperature: 0.2,
    },
    generationContext
  );

  return {
    imageUrl: result.thumbnailUrl || result.image || null,
    prompt,
  };
}

async function callGeminiForCustomRoom(
  compositeDataUrl: string,
  productDataUrl: string,
  plan: CustomRoomPlan,
  customPrompt: string,
  includeRoomReference: boolean,
  roomReferenceDataUrl?: string | null,
  subjectFraming?: string
): Promise<{
  thumbnailUrl: string | null;
  text: string | null;
  prompt: string;
  debugPayload: ApiDebugPayload;
}> {
  const generationContext = snapshotGenerationContext();
  const compositePath = await uploadDataUrlToGCS(
    compositeDataUrl,
    buildTempPath("custom_composite")
  );
  const productPath = await uploadDataUrlToGCS(
    productDataUrl,
    buildTempPath("custom_product")
  );
  const roomReferencePath =
    includeRoomReference && roomReferenceDataUrl
      ? await uploadDataUrlToGCS(
          roomReferenceDataUrl,
          buildTempPath("custom_room_ref")
        )
      : undefined;

  const roomReferenceBlock =
    includeRoomReference && roomReferenceDataUrl
      ? "Image 3 (Room Reference): the original custom room style photo for materials, lighting, furniture character, and atmosphere.\n\n"
      : "";
  const styleRecoveryInstruction =
    includeRoomReference && roomReferenceDataUrl
      ? "Use the room photo only to recover believable materials, styling, and lighting. Do not change the camera angle, crop, or product size established in Image 1."
      : `Do not expect a room photo reference. Infer photoreal materials, furniture styling, and lighting only from Image 1 and this room context: ${plan.roomContext}.`;

  const basePrompt = `Image 1 (Placement Preview): the exact line-art room composition, product placement, scale, and crop. Treat this as the source of truth for geometry and framing.
Image 2 (Product): the definitive product appearance reference.
${roomReferenceBlock}Instructions:
Render a photorealistic version of the intended room while matching the composition and product framing from Image 1 exactly.
Room type: ${plan.roomType}
Room style: ${plan.styleLabel}
Estimated room dimensions: ${plan.estimatedRoomDimensionsCm.width}cm wide, ${plan.estimatedRoomDimensionsCm.height}cm high, ${plan.estimatedRoomDimensionsCm.depth}cm deep.
Placement scale note: ${plan.placement.estimatedProductScaleNote}

Keep the product in the exact same placement box shown in Image 1 on the ${plan.placement.surfaceLabel}.
The product must be the main centered focal point of the image. Respect this framing: ${subjectFraming || plan.camera.subjectFraming}.
${styleRecoveryInstruction}
Preserve the exact product shape, color, and finish from Image 2.
Lighting and shadows must ground the product naturally on the ${plan.placement.surfaceLabel}.
Output one clean photorealistic interior image with no sketch lines, no grid, and no extra duplicate products.`;

  const prompt = customPrompt.trim()
    ? `${basePrompt}\n\nAdditional user direction:\n${customPrompt.trim()}`
    : basePrompt;

  const references = roomReferencePath
    ? [productPath, roomReferencePath]
    : [productPath];

  const request: ApiDebugPayload["request"] = {
    mode: "free_prompt",
    images: {
      input: compositePath,
      references,
    },
    params: {
      prompt,
      rawPrompt: true,
      aspectRatio: "1:1",
      temperature: 0.35,
    },
  };

  const result = await callGenerateImage(
    request.mode,
    request.images,
    request.params,
    generationContext
  );

  return {
    thumbnailUrl: result.thumbnailUrl || result.image || null,
    text: null,
    prompt,
    debugPayload: {
      request,
      prompt,
      assets: [
        {
          label: "Placement",
          filename: "image-1-placement.png",
          mimeType: "image/png",
          dataUrl: compositeDataUrl,
        },
        {
          label: "Product",
          filename: "image-2-product.png",
          mimeType: "image/png",
          dataUrl: productDataUrl,
        },
        ...(includeRoomReference && roomReferenceDataUrl
          ? [
              {
                label: "Room Reference",
                filename: "image-3-room-reference.png",
                mimeType: "image/png",
                dataUrl: roomReferenceDataUrl,
              },
            ]
          : []),
      ],
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type DimensionMode = "both" | "height" | "width";
type PlacementTarget = "none" | "product" | `scene-object:${string}`;

const STAGING_SURFACE_OPTIONS: {
  id: StagingSurfaceOverride;
  label: string;
}[] = [
  { id: "auto", label: "Auto" },
  { id: "display-shelf", label: "Shelf" },
  { id: "sideboard", label: "Sideboard" },
  { id: "display-plinth", label: "Plinth" },
  { id: "coffee-table", label: "Coffee Table" },
  { id: "floor", label: "Floor" },
];
type PreviewInteractionMode = "camera" | "grab";

function DemoScalingPage() {
  const user = auth.currentUser;
  const isDev = import.meta.env.DEV;
  const { data: brandsData, isLoading: isLoadingBrands } =
    useListBrandTemplates({
      enabled: !!user,
    });
  const globalBrands = React.useMemo<BrandIdentity[]>(() => {
    if (!brandsData?.brandTemplates) return [];
    return brandsData.brandTemplates.map((template) =>
      deserializeBrandTemplate(template as any)
    );
  }, [brandsData]);

  const [roomDimensions, setRoomDimensions] = React.useState<RoomDimensions>({
    width: DEFAULT_ROOM_W,
    height: DEFAULT_ROOM_H,
    depth: DEFAULT_ROOM_D,
  });
  // The custom-room pipeline stays in the file for later, but the live demo is
  // intentionally empty-room only to keep the active flow focused.
  const roomStyleId = "empty-room" as RoomStyleId;
  const [selectedBrandId, setSelectedBrandId] = React.useState<string>("");
  const [selectedVibeId, setSelectedVibeId] = React.useState<string>("");
  const [selectedSceneId, setSelectedSceneId] = React.useState<string>("");
  const [framingPreference, setFramingPreference] =
    React.useState<FramingPreference>("balanced");
  const [roomPlacementPreference, setRoomPlacementPreference] =
    React.useState<RoomPlacementPreference>("center");
  const [roomViewAngle, setRoomViewAngle] =
    React.useState<RoomViewAngle>("frontal");
  const [stagingSurfaceOverride, setStagingSurfaceOverride] =
    React.useState<StagingSurfaceOverride>("auto");
  const [userMode, setUserMode] = React.useState(true);
  const canToggleMode =
    isDev || (user?.email?.endsWith("@shop-vibes.de") ?? false);
  const [camera, setCamera] = React.useState<Camera3D>(
    defaultCameraForRoom({
      width: DEFAULT_ROOM_W,
      height: DEFAULT_ROOM_H,
      depth: DEFAULT_ROOM_D,
    })
  );
  const [productImage, setProductImage] = React.useState<string | null>(null);
  const [productFileName, setProductFileName] = React.useState<string>("");
  const [productAnalysis, setProductAnalysis] = React.useState<{
    category: string;
    description: string;
  } | null>(null);
  const [isAnalyzingProduct, setIsAnalyzingProduct] = React.useState(false);
  const [stagingProfile, setStagingProfile] =
    React.useState<ProductStagingProfile | null>(null);
  const [imageAspectRatio, setImageAspectRatio] = React.useState<number>(1);
  const [dimensionMode, setDimensionMode] =
    React.useState<DimensionMode>("height");
  const [productHeightCm, setProductHeightCm] = React.useState<number>(100);
  const [productWidthCm, setProductWidthCm] = React.useState<number>(40);
  const [customPrompt, setCustomPrompt] = React.useState<string>("");
  const [productPosition, setProductPosition] =
    React.useState<ProductPosition3D>(
      defaultProductPosForRoom({
        width: DEFAULT_ROOM_W,
        height: DEFAULT_ROOM_H,
        depth: DEFAULT_ROOM_D,
      })
    );
  const [snapLabel, setSnapLabel] = React.useState<string>("floor");
  const [snapSceneObjectId, setSnapSceneObjectId] = React.useState<
    string | null
  >(null);
  const [placementTarget, setPlacementTarget] =
    React.useState<PlacementTarget>("none");
  const [previewInteractionMode, setPreviewInteractionMode] =
    React.useState<PreviewInteractionMode>("grab");
  const [sceneObjects, setSceneObjects] = React.useState<SceneObjectInstance[]>(
    []
  );
  const nextSceneObjectIdRef = React.useRef(1);
  const [imageTrimmed, setImageTrimmed] = React.useState(false);
  const [customRoomReference, setCustomRoomReference] = React.useState<
    string | null
  >(null);
  const [customRoomReferenceName, setCustomRoomReferenceName] =
    React.useState<string>("");
  const [customRoomPlan, setCustomRoomPlan] =
    React.useState<CustomRoomPlan | null>(null);
  const [customRoomPlacementRect, setCustomRoomPlacementRect] =
    React.useState<NormalizedRect | null>(null);
  const [customRoomLineArt, setCustomRoomLineArt] = React.useState<
    string | null
  >(null);
  const [
    includeRoomReferenceInGeneration,
    setIncludeRoomReferenceInGeneration,
  ] = React.useState(false);
  const [isAnalyzingCustomRoom, setIsAnalyzingCustomRoom] =
    React.useState(false);
  const [isGeneratingCustomLineArt, setIsGeneratingCustomLineArt] =
    React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [resultImage, setResultImage] = React.useState<string | null>(null);
  const [logs, setLogs] = React.useState<LogEntry[]>([]);
  const [lastApiPayload, setLastApiPayload] =
    React.useState<ApiDebugPayload | null>(null);

  const sketchCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const [sketchDataUrl, setSketchDataUrl] = React.useState<string | null>(null);
  const [compositeDataUrl, setCompositeDataUrl] = React.useState<string | null>(
    null
  );

  // Debounce timer for sketch log entries (avoids flooding during camera orbit/scroll)
  const logSketchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Captures camera state at the start of each drag so orbit deltas are absolute
  const orbitDragStartRef = React.useRef<{
    azimuth: number;
    elevation: number;
  }>({ azimuth: 0, elevation: 0 });

  const effectiveHeight =
    dimensionMode === "width"
      ? Math.round(productWidthCm / imageAspectRatio)
      : productHeightCm;
  const effectiveWidth =
    dimensionMode === "height"
      ? Math.round(productHeightCm * imageAspectRatio)
      : productWidthCm;

  const currentBrand = React.useMemo(
    () =>
      globalBrands.find((brand) => brand.id === selectedBrandId) ??
      globalBrands[0] ??
      null,
    [globalBrands, selectedBrandId]
  );
  const selectedVibe = React.useMemo(
    () =>
      currentBrand?.vibes.find((vibe) => vibe.id === selectedVibeId) ??
      currentBrand?.vibes[0] ??
      null,
    [currentBrand, selectedVibeId]
  );
  const selectedScene = React.useMemo(
    () =>
      currentBrand?.scenes.find((scene) => scene.id === selectedSceneId) ??
      currentBrand?.scenes[0] ??
      null,
    [currentBrand, selectedSceneId]
  );
  const brandOptions = React.useMemo(
    () =>
      globalBrands.map((brand) => ({
        value: brand.id,
        label: brand.name,
        description: brand.context,
      })),
    [globalBrands]
  );
  const vibeOptions = React.useMemo(
    () =>
      (currentBrand?.vibes ?? []).map((vibe) => ({
        value: vibe.id,
        label: vibe.label,
        description: vibe.description,
      })),
    [currentBrand]
  );
  const sceneOptions = React.useMemo(
    () =>
      (currentBrand?.scenes ?? []).map((scene) => ({
        value: scene.id,
        label: scene.label,
        description: scene.context,
      })),
    [currentBrand]
  );
  const mappedEmptyRoomAestheticId = React.useMemo(
    () => mapBrandVibeToAesthetic(selectedVibe),
    [selectedVibe]
  );
  const mappedEmptyRoomInteriorType = React.useMemo(
    () => mapSceneToInteriorType(selectedScene),
    [selectedScene]
  );
  const mappedEmptyRoomAesthetic =
    EMPTY_ROOM_AESTHETICS.find(
      (style) => style.id === mappedEmptyRoomAestheticId
    ) ?? EMPTY_ROOM_AESTHETICS[0];
  const mappedInteriorLabel =
    INTERIOR_TYPES.find((type) => type.id === mappedEmptyRoomInteriorType)
      ?.label ?? "Living Room";
  const selectedSceneVibeContext = React.useMemo<SceneVibePromptContext>(
    () => ({
      vibeLabel: selectedVibe?.label || mappedEmptyRoomAesthetic.label,
      vibeDescription: selectedVibe?.description || "",
      sceneLabel: selectedScene?.label || mappedInteriorLabel,
      sceneContext: selectedScene?.context || "",
    }),
    [
      selectedScene,
      selectedVibe,
      mappedEmptyRoomAesthetic.label,
      mappedInteriorLabel,
    ]
  );

  React.useEffect(() => {
    if (globalBrands.length === 0) {
      if (selectedBrandId !== "") setSelectedBrandId("");
      return;
    }
    const hasSelectedBrand = globalBrands.some(
      (brand) => brand.id === selectedBrandId
    );
    if (!hasSelectedBrand) {
      setSelectedBrandId(globalBrands[0].id);
    }
  }, [globalBrands, selectedBrandId]);

  React.useEffect(() => {
    if (!currentBrand) {
      if (selectedVibeId !== "") setSelectedVibeId("");
      if (selectedSceneId !== "") setSelectedSceneId("");
      return;
    }

    const hasSelectedVibe = currentBrand.vibes.some(
      (vibe) => vibe.id === selectedVibeId
    );
    if (!hasSelectedVibe) {
      const nextVibeId = currentBrand.vibes[0]?.id ?? "";
      if (nextVibeId !== selectedVibeId) {
        setSelectedVibeId(nextVibeId);
      }
    }

    const hasSelectedScene = currentBrand.scenes.some(
      (scene) => scene.id === selectedSceneId
    );
    if (!hasSelectedScene) {
      const nextSceneId = currentBrand.scenes[0]?.id ?? "";
      if (nextSceneId !== selectedSceneId) {
        setSelectedSceneId(nextSceneId);
      }
    }
  }, [currentBrand, selectedSceneId, selectedVibeId]);

  const activeRoomStyle =
    ROOM_STYLES.find((s) => s.id === roomStyleId) ?? ROOM_STYLES[0];
  const isCustomRoomStyle = roomStyleId === "custom-room";
  const effectiveCustomPlacementRect =
    customRoomPlacementRect ?? customRoomPlan?.placement.rect ?? null;
  const effectiveCustomSubjectFraming = customRoomPlan
    ? appendSentence(
        customRoomPlan.camera.subjectFraming,
        getFramingPreferenceConfig(framingPreference).subjectSentence
      )
    : null;
  const effectiveCustomCameraReason = customRoomPlan
    ? appendSentence(
        customRoomPlan.camera.reason,
        getFramingPreferenceConfig(framingPreference).reasonSentence
      )
    : null;
  const effectiveCustomCameraRect =
    effectiveCustomPlacementRect && customRoomPlan
      ? fitCameraRectAroundProduct(
          effectiveCustomPlacementRect,
          customRoomPlan.camera.rect,
          framingPreference
        )
      : null;

  const sceneBoxes = React.useMemo(
    () =>
      sceneObjects.map((instance) =>
        buildSceneBox(
          getSceneObjectDefinition(instance.defId),
          instance,
          roomDimensions,
          instance.instanceId
        )
      ),
    [sceneObjects, roomDimensions]
  );

  const sceneObjectOverlays = React.useMemo(
    () =>
      sceneBoxes
        .map((box) => projectSceneBoxOverlay(box, camera))
        .filter((overlay): overlay is SceneObjectOverlay => Boolean(overlay)),
    [sceneBoxes, camera]
  );

  const productOverlay = React.useMemo(() => {
    if (!productImage) return null;
    if (isCustomRoomStyle) {
      if (!effectiveCustomPlacementRect || !effectiveCustomCameraRect) {
        return null;
      }
      const rect = rectToCropSpace(
        effectiveCustomPlacementRect,
        effectiveCustomCameraRect
      );
      if (!rect) return null;
      const fittedRect = fitRectPreservingAspect(rect, imageAspectRatio);
      return {
        id: "product",
        label: "product",
        left: fittedRect.left,
        top: fittedRect.top,
        width: fittedRect.width,
        height: fittedRect.height,
      };
    }
    return projectProductOverlay(
      productPosition,
      effectiveWidth,
      effectiveHeight,
      camera
    );
  }, [
    productImage,
    productPosition,
    effectiveWidth,
    effectiveHeight,
    camera,
    isCustomRoomStyle,
    effectiveCustomPlacementRect,
    effectiveCustomCameraRect,
    imageAspectRatio,
  ]);

  const selectedSceneObjectId = placementTarget.startsWith("scene-object:")
    ? placementTarget.replace("scene-object:", "")
    : null;

  const selectedSceneObject = React.useMemo(
    () =>
      selectedSceneObjectId
        ? (sceneObjects.find(
            (instance) => instance.instanceId === selectedSceneObjectId
          ) ?? null)
        : null,
    [sceneObjects, selectedSceneObjectId]
  );

  const snapSurfaces = React.useMemo(
    () => [...activeRoomStyle.surfaces, ...sceneBoxes.map(sceneBoxToSurface)],
    [activeRoomStyle.surfaces, sceneBoxes]
  );

  const addLog = React.useCallback(
    (label: string, detail?: string, imageDataUrl?: string) => {
      const time = new Date().toISOString().split("T")[1].slice(0, 12);
      setLogs((prev) => [...prev, { time, label, detail, imageDataUrl }]);
      console.log(`[DEMO ${time}] ${label}`, detail ?? "");
    },
    []
  );

  /** Called by UnifiedPreview at the start of each drag to snapshot current camera state */
  const handleOrbitDragStart = React.useCallback(() => {
    orbitDragStartRef.current = {
      azimuth: camera.azimuth,
      elevation: camera.elevation,
    };
  }, [camera.azimuth, camera.elevation]);

  /** Called by UnifiedPreview during a drag with total pixel delta from drag start */
  const handleCameraOrbit = React.useCallback(
    (totalDx: number, totalDy: number) => {
      const start = orbitDragStartRef.current;
      setCamera((c) => ({
        ...c,
        azimuth: Math.max(-60, Math.min(60, start.azimuth + totalDx * 0.35)),
        elevation: Math.max(5, Math.min(70, start.elevation - totalDy * 0.3)),
      }));
    },
    []
  );

  /** Called by UnifiedPreview on scroll wheel */
  const handleCameraZoom = React.useCallback((deltaY: number) => {
    setCamera((c) => ({
      ...c,
      distance: Math.max(80, Math.min(1000, c.distance * (1 + deltaY * 0.001))),
    }));
  }, []);

  // Active room label for the empty-room demo flow.
  const effectiveRoomStyle = React.useMemo(() => {
    const labels = [
      selectedSceneVibeContext.sceneLabel,
      selectedSceneVibeContext.vibeLabel,
    ].filter(Boolean);

    return {
      ...activeRoomStyle,
      label:
        labels.join(" · ") ||
        `${mappedEmptyRoomAesthetic.label} ${mappedInteriorLabel}`.trim(),
    };
  }, [
    activeRoomStyle,
    selectedSceneVibeContext.sceneLabel,
    selectedSceneVibeContext.vibeLabel,
    mappedEmptyRoomAesthetic.label,
    mappedInteriorLabel,
  ]);

  // Generate room sketch when style or camera changes.
  // The canvas is redrawn immediately for a smooth preview; the debug log entry
  // is debounced (600 ms) so rapid camera/zoom/pan changes produce only one entry.
  React.useEffect(() => {
    if (isCustomRoomStyle) {
      setSketchDataUrl(customRoomLineArt);
      if (customRoomLineArt) {
        addLog(
          "Custom room line art ready",
          customRoomPlan
            ? `${customRoomPlan.styleLabel} · ${customRoomPlan.roomType}`
            : "Custom room preview prepared",
          customRoomLineArt
        );
      }
      return;
    }
    if (!sketchCanvasRef.current) return;

    const dataUrl = drawRoom3D(
      sketchCanvasRef.current,
      camera,
      roomStyleId,
      roomDimensions,
      sceneBoxes
    );
    setSketchDataUrl(dataUrl);

    if (logSketchTimerRef.current) clearTimeout(logSketchTimerRef.current);
    logSketchTimerRef.current = setTimeout(() => {
      addLog(
        "3D room sketch",
        `${effectiveRoomStyle.label} — az:${camera.azimuth}° el:${camera.elevation}° dist:${camera.distance} fov:${camera.fov}°`,
        dataUrl
      );
    }, 600);
  }, [
    roomStyleId,
    camera,
    roomDimensions,
    effectiveRoomStyle.label,
    addLog,
    sceneBoxes,
    isCustomRoomStyle,
    customRoomLineArt,
    customRoomPlan,
  ]);

  // Re-composite when inputs change
  React.useEffect(() => {
    if (!productImage) {
      setCompositeDataUrl(null);
      return;
    }
    let cancelled = false;
    const compositePromise =
      isCustomRoomStyle &&
      customRoomLineArt &&
      effectiveCustomPlacementRect &&
      effectiveCustomCameraRect
        ? compositeProductOnCustomRoom(
            customRoomLineArt,
            productImage,
            effectiveCustomPlacementRect,
            effectiveCustomCameraRect,
            imageAspectRatio
          )
        : sketchDataUrl
          ? compositeProduct3D(
              sketchDataUrl,
              productImage,
              effectiveHeight,
              effectiveWidth,
              productPosition,
              camera
            )
          : Promise.resolve<string | null>(null);
    compositePromise.then((url) => {
      if (!cancelled) setCompositeDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [
    sketchDataUrl,
    productImage,
    effectiveHeight,
    effectiveWidth,
    productPosition,
    camera,
    isCustomRoomStyle,
    customRoomLineArt,
    effectiveCustomPlacementRect,
    effectiveCustomCameraRect,
    imageAspectRatio,
  ]);

  const handleProductPositionCommit = React.useCallback(
    (nextPosition: ProductPosition3D, nextSnapLabel: string) => {
      if (!compositeDataUrl) return;
      addLog(
        "Composite updated",
        `Product at (${nextPosition.x.toFixed(0)}, ${nextPosition.z.toFixed(0)}) cm · surface: ${nextSnapLabel} (y=${nextPosition.y}cm)`,
        compositeDataUrl
      );
    },
    [compositeDataUrl, addLog]
  );

  const handleSceneObjectCommit = React.useCallback(
    (box: SceneBox) => {
      addLog(
        `${box.label} moved`,
        `${box.label} at (${box.origin[0].toFixed(0)}, ${box.origin[1].toFixed(0)}, ${box.origin[2].toFixed(0)}) cm`
      );
    },
    [addLog]
  );

  const handleAddSceneObject = React.useCallback(
    (def: SceneObjectDefinition) => {
      const instanceId = `${def.id}-${nextSceneObjectIdRef.current++}`;
      const nextState: SceneObjectInstance = {
        instanceId,
        defId: def.id,
        origin: clampSceneObjectOrigin(
          def,
          { rotation: 0, wall: "back" },
          def.defaultOrigin,
          roomDimensions
        ),
        rotation: 0,
        wall: "back",
      };
      setSceneObjects((current) => [...current, nextState]);
      setPlacementTarget(`scene-object:${instanceId}`);
      setPreviewInteractionMode("grab");
      addLog(
        `${def.label} added`,
        `${def.label} at (${nextState.origin[0].toFixed(0)}, ${nextState.origin[1].toFixed(0)}, ${nextState.origin[2].toFixed(0)}) cm`
      );
    },
    [addLog, roomDimensions]
  );

  const handleRemoveSceneObject = React.useCallback(
    (instanceId: string) => {
      const instance = sceneObjects.find(
        (item) => item.instanceId === instanceId
      );
      if (!instance) return;
      const def = getSceneObjectDefinition(instance.defId);
      const wasSelected = placementTarget === `scene-object:${instanceId}`;
      const productWasOnSceneObject = snapSceneObjectId === instanceId;
      setSceneObjects((current) =>
        current.filter((item) => item.instanceId !== instanceId)
      );
      if (wasSelected) setPlacementTarget("none");
      if (productWasOnSceneObject) {
        setSnapLabel("floor");
        setSnapSceneObjectId(null);
        setProductPosition(defaultProductPosForRoom(roomDimensions));
      }
      addLog(`${def.label} removed`);
    },
    [addLog, placementTarget, snapSceneObjectId, sceneObjects, roomDimensions]
  );

  const handleRotateSceneObject = React.useCallback(
    (instanceId: string) => {
      setSceneObjects((current) =>
        current.map((existing) => {
          if (existing.instanceId !== instanceId) return existing;
          const def = getSceneObjectDefinition(existing.defId);
          const nextRotation = rotate90(existing.rotation);
          return {
            ...existing,
            rotation: nextRotation,
            origin: clampSceneObjectOrigin(
              def,
              { rotation: nextRotation, wall: existing.wall },
              existing.origin,
              roomDimensions
            ),
          };
        })
      );
      const instance = sceneObjects.find(
        (item) => item.instanceId === instanceId
      );
      if (instance)
        addLog(`${getSceneObjectDefinition(instance.defId).label} rotated`);
    },
    [addLog, sceneObjects, roomDimensions]
  );

  const handleCycleSceneObjectWall = React.useCallback(
    (instanceId: string) => {
      setSceneObjects((current) =>
        current.map((existing) => {
          if (existing.instanceId !== instanceId) return existing;
          const def = getSceneObjectDefinition(existing.defId);
          const wallOrder: SceneWall[] = ["back", "left", "right"];
          const nextWall =
            wallOrder[
              (wallOrder.indexOf(existing.wall) + 1) % wallOrder.length
            ];
          return {
            ...existing,
            wall: nextWall,
            origin: clampSceneObjectOrigin(
              def,
              { rotation: existing.rotation, wall: nextWall },
              existing.origin,
              roomDimensions
            ),
          };
        })
      );
      const instance = sceneObjects.find(
        (item) => item.instanceId === instanceId
      );
      if (instance)
        addLog(
          `${getSceneObjectDefinition(instance.defId).label} wall changed`
        );
    },
    [addLog, sceneObjects, roomDimensions]
  );

  const handlePlacement = React.useCallback(
    (
      fracX: number,
      fracY: number,
      commit = true,
      targetOverride?: PlacementTarget
    ) => {
      const activeTarget = targetOverride ?? placementTarget;
      if (
        isCustomRoomStyle &&
        activeTarget !== "none" &&
        activeTarget === "product" &&
        customRoomPlan &&
        effectiveCustomPlacementRect &&
        effectiveCustomCameraRect
      ) {
        const point = cropSpacePointToRoomSpace(
          fracX,
          fracY,
          effectiveCustomCameraRect
        );
        const nextRect = normalizeRect({
          left: point.x - effectiveCustomPlacementRect.width / 2,
          top: point.y - effectiveCustomPlacementRect.height / 2,
          width: effectiveCustomPlacementRect.width,
          height: effectiveCustomPlacementRect.height,
        });
        setCustomRoomPlacementRect(nextRect);
        setSnapLabel(customRoomPlan.placement.surfaceLabel);
        if (commit) {
          addLog(
            "Custom placement updated",
            JSON.stringify(
              {
                surface: customRoomPlan.placement.surfaceLabel,
                reason: customRoomPlan.placement.reason,
                rect: nextRect,
                camera: fitCameraRectAroundProduct(
                  nextRect,
                  customRoomPlan.camera.rect,
                  framingPreference
                ),
                framingPreference,
              },
              null,
              2
            )
          );
        }
        return;
      }

      const sx = fracX * CANVAS_PX;
      const sy = fracY * CANVAS_PX;

      if (activeTarget.startsWith("scene-object:")) {
        const objectId = activeTarget.replace("scene-object:", "");
        const currentState =
          sceneObjects.find((instance) => instance.instanceId === objectId) ??
          null;
        if (!currentState) return;
        const def = getSceneObjectDefinition(currentState.defId);
        const currentBox = buildSceneBox(
          def,
          currentState,
          roomDimensions,
          currentState.instanceId
        );
        const nextOrigin = placeSceneObjectFromScreen(
          def,
          currentState,
          sx,
          sy,
          camera,
          roomDimensions
        );
        const nextState = { ...currentState, origin: nextOrigin };
        const nextBox = buildSceneBox(
          def,
          nextState,
          roomDimensions,
          nextState.instanceId
        );
        if (snapSceneObjectId === currentState.instanceId) {
          const dx = nextBox.origin[0] - currentBox.origin[0];
          const dy = nextBox.origin[1] - currentBox.origin[1];
          const dz = nextBox.origin[2] - currentBox.origin[2];
          setProductPosition((current) => ({
            x: current.x + dx,
            y: current.y + dy,
            z: current.z + dz,
          }));
        }
        setSceneObjects((current) =>
          current.map((instance) =>
            instance.instanceId === objectId ? nextState : instance
          )
        );
        if (commit) handleSceneObjectCommit(nextBox);
        return;
      }

      const hit = unprojectToSurfaces(
        sx,
        sy,
        camera,
        snapSurfaces,
        roomDimensions
      );
      const nextPosition = { x: hit.x, y: hit.y, z: hit.z };
      setProductPosition(nextPosition);
      setSnapLabel(hit.label);
      setSnapSceneObjectId(hit.id ?? null);
      if (commit) handleProductPositionCommit(nextPosition, hit.label);
    },
    [
      placementTarget,
      camera,
      roomDimensions,
      sceneObjects,
      snapSceneObjectId,
      snapSurfaces,
      handleSceneObjectCommit,
      handleProductPositionCommit,
    ]
  );

  const handleSelectSceneObject = React.useCallback((objectId: string) => {
    setPlacementTarget(`scene-object:${objectId}`);
    setPreviewInteractionMode("grab");
  }, []);

  const handleSelectTarget = React.useCallback((target: PlacementTarget) => {
    setPlacementTarget(target);
    setPreviewInteractionMode("grab");
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName;
      if (
        target?.isContentEditable ||
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT"
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === "v") {
        event.preventDefault();
        setPreviewInteractionMode("camera");
      } else if (key === "g") {
        event.preventDefault();
        setPreviewInteractionMode("grab");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  React.useEffect(() => {
    if (!isCustomRoomStyle) return;
    setPreviewInteractionMode("grab");
    setPlacementTarget("product");
  }, [isCustomRoomStyle]);

  React.useEffect(() => {
    setCamera((current) => ({
      ...current,
      target: [
        Math.min(roomDimensions.width, Math.max(0, current.target[0])),
        Math.min(roomDimensions.height, Math.max(0, current.target[1])),
        Math.min(roomDimensions.depth, Math.max(0, current.target[2])),
      ],
    }));
    setProductPosition((current) => ({
      x: Math.min(roomDimensions.width, Math.max(0, current.x)),
      y: current.y,
      z: Math.min(roomDimensions.depth, Math.max(0, current.z)),
    }));
    setSceneObjects((current) =>
      current.map((instance) => {
        const def = getSceneObjectDefinition(instance.defId);
        return {
          ...instance,
          origin: clampSceneObjectOrigin(
            def,
            instance,
            instance.origin,
            roomDimensions
          ),
        };
      })
    );
  }, [roomDimensions]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProductFileName(file.name);
    setImageTrimmed(false);
    setProductAnalysis(null);
    setStagingProfile(null);
    setIsAnalyzingProduct(true);
    const rawDataUrl = await readFileAsDataUrl(file);
    const { dataUrl, trimmed, originalW, originalH, trimmedW, trimmedH } =
      await trimTransparentPadding(rawDataUrl);
    const ratio = trimmedW / trimmedH;
    setImageAspectRatio(ratio);
    setProductImage(dataUrl);
    setImageTrimmed(trimmed);
    addLog(
      "Product image uploaded",
      trimmed
        ? `${file.name} (${(file.size / 1024).toFixed(1)}KB) — trimmed transparent padding: ${originalW}×${originalH}px → ${trimmedW}×${trimmedH}px, ratio ${ratio.toFixed(3)}`
        : `${file.name} (${(file.size / 1024).toFixed(1)}KB) — ${originalW}×${originalH}px, ratio ${ratio.toFixed(3)} (no padding detected)`,
      dataUrl
    );

    try {
      addLog(
        "Product staging analysis started",
        "Running Gemini staging analysis (orientation, placement, camera)"
      );
      const imagePath = await uploadDataUrlToGCS(
        dataUrl,
        buildTempPath("staging_product")
      );
      const staging = await callAnalysis<ProductStagingProfile>(
        "analyze_product_staging",
        { image: imagePath },
        {
          productHeightCm: productHeightCm,
          productWidthCm: Math.round(productHeightCm * ratio),
        }
      );
      const normalized: ProductStagingProfile = {
        category: staging.category?.trim() || "product",
        description:
          staging.description?.trim() || "No product description returned.",
        viewAngle: staging.viewAngle?.trim() || "front",
        cameraHeight: staging.cameraHeight?.trim() || "eye-level",
        suggestedAzimuthDeg: Math.max(
          -45,
          Math.min(45, Number(staging.suggestedAzimuthDeg) || 0)
        ),
        suggestedElevationDeg: Math.max(
          0,
          Math.min(45, Number(staging.suggestedElevationDeg) || 15)
        ),
        isSymmetric: Boolean(staging.isSymmetric),
        orientationReason: staging.orientationReason?.trim() || "",
        placementType: staging.placementType?.trim() || "tabletop",
        suggestedSupport: staging.suggestedSupport?.trim() || "sideboard",
        roomPlacement: staging.roomPlacement?.trim() || "focal-point",
        stagingReason: staging.stagingReason?.trim() || "",
        visualWeight: staging.visualWeight?.trim() || "medium-object",
        weightReason: staging.weightReason?.trim() || "",
      };
      setStagingProfile(normalized);
      // Keep productAnalysis for backward compat with custom room flow
      setProductAnalysis({
        category: normalized.category,
        description: normalized.description,
      });
      addLog(
        "Product staging analysis complete",
        JSON.stringify(
          {
            category: normalized.category,
            description: normalized.description,
            orientation: {
              viewAngle: normalized.viewAngle,
              cameraHeight: normalized.cameraHeight,
              azimuth: normalized.suggestedAzimuthDeg,
              elevation: normalized.suggestedElevationDeg,
              symmetric: normalized.isSymmetric,
              reason: normalized.orientationReason,
            },
            staging: {
              placement: normalized.placementType,
              support: normalized.suggestedSupport,
              roomPlacement: normalized.roomPlacement,
              reason: normalized.stagingReason,
            },
            weight: {
              visual: normalized.visualWeight,
              reason: normalized.weightReason,
            },
          },
          null,
          2
        )
      );
    } catch (error) {
      setStagingProfile(null);
      setProductAnalysis(null);
      addLog(
        "Product staging analysis failed",
        error instanceof Error ? error.message : String(error)
      );
    } finally {
      setIsAnalyzingProduct(false);
    }
  };

  const handleCustomRoomUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setCustomRoomReference(dataUrl);
    setCustomRoomReferenceName(file.name);
    setCustomRoomPlan(null);
    setCustomRoomPlacementRect(null);
    setCustomRoomLineArt(null);
    addLog(
      "Custom room reference uploaded",
      `${file.name} (${(file.size / 1024).toFixed(1)}KB)`,
      dataUrl
    );
  };

  const handleBuildCustomRoomWorkflow = React.useCallback(async () => {
    if (!customRoomReference) return;
    setIsAnalyzingCustomRoom(true);
    setIsGeneratingCustomLineArt(false);
    try {
      addLog(
        "Custom room analysis started",
        productImage
          ? `Sending room + product to Gemini for placement, surface, and framing analysis. Requested shot: ${framingPreference}.`
          : `Sending room to Gemini for room planning. Uploading a product later will improve placement accuracy. Requested shot: ${framingPreference}.`
      );
      const plan = await analyzeCustomRoomStyle(
        customRoomReference,
        productImage,
        {
          productCategory: productAnalysis?.category || "generic decor object",
          productDescription:
            productAnalysis?.description ||
            "A product will be added later. Choose the most plausible merchandised focal surface.",
          productHeightCm: effectiveHeight,
          productWidthCm: effectiveWidth,
        }
      );
      const deterministicPlacement = buildDeterministicPlacement({
        plan,
        productAnalysis,
        productHeightCm: effectiveHeight,
        imageAspectRatio,
      });
      const resolvedPlan: CustomRoomPlan = {
        ...plan,
        placement: {
          ...plan.placement,
          surfaceLabel: deterministicPlacement.surface.label,
          surfaceType: deterministicPlacement.surface.surfaceType,
          reason: deterministicPlacement.reason,
          rect: deterministicPlacement.rect,
        },
      };
      const resolvedCameraRect = fitCameraRectAroundProduct(
        resolvedPlan.placement.rect,
        resolvedPlan.camera.rect,
        framingPreference
      );
      const resolvedSubjectFraming = appendSentence(
        resolvedPlan.camera.subjectFraming,
        getFramingPreferenceConfig(framingPreference).subjectSentence
      );
      const resolvedCameraReason = appendSentence(
        resolvedPlan.camera.reason,
        getFramingPreferenceConfig(framingPreference).reasonSentence
      );

      setCustomRoomPlan(resolvedPlan);
      setRoomDimensions({
        width: resolvedPlan.estimatedRoomDimensionsCm.width,
        height: resolvedPlan.estimatedRoomDimensionsCm.height,
        depth: resolvedPlan.estimatedRoomDimensionsCm.depth,
      });
      setCustomRoomPlacementRect(resolvedPlan.placement.rect);
      setSnapLabel(resolvedPlan.placement.surfaceLabel);
      addLog(
        "Custom room analysis completed",
        JSON.stringify(
          {
            style: resolvedPlan.styleLabel,
            roomType: resolvedPlan.roomType,
            estimatedRoomDimensionsCm: resolvedPlan.estimatedRoomDimensionsCm,
            modelSuggestedPlacement: {
              surface: plan.placement.surfaceLabel,
              surfaceType: plan.placement.surfaceType,
              rect: plan.placement.rect,
              reason: plan.placement.reason,
            },
            placement: {
              surface: resolvedPlan.placement.surfaceLabel,
              surfaceType: resolvedPlan.placement.surfaceType,
              rect: resolvedPlan.placement.rect,
              reason: resolvedPlan.placement.reason,
              estimatedProductScaleNote:
                resolvedPlan.placement.estimatedProductScaleNote,
            },
            camera: {
              rect: resolvedCameraRect,
              reason: resolvedCameraReason,
              framing: resolvedSubjectFraming,
            },
            surfaces: resolvedPlan.surfaces,
            framingPreference,
          },
          null,
          2
        )
      );

      setIsGeneratingCustomLineArt(true);
      addLog("Custom line art generation started", resolvedPlan.lineArtPrompt);
      const lineArt = await generateCustomRoomLineArt(
        customRoomReference,
        resolvedPlan,
        productAnalysis,
        resolvedSubjectFraming
      );
      addLog("Custom line art prompt", lineArt.prompt);
      if (lineArt.imageUrl) {
        const lineArtDataUrl = await ensureDataUrl(lineArt.imageUrl);
        setCustomRoomLineArt(lineArtDataUrl);
        addLog(
          "Custom line art generated",
          `${resolvedPlan.styleLabel} preview prepared`,
          lineArtDataUrl
        );
      } else {
        addLog("Custom line art missing", "Gemini returned no preview image.");
      }
    } catch (error) {
      addLog(
        "Custom room workflow failed",
        error instanceof Error ? error.message : String(error)
      );
      console.error("[DEMO] Custom room workflow failed:", error);
    } finally {
      setIsAnalyzingCustomRoom(false);
      setIsGeneratingCustomLineArt(false);
    }
  }, [
    addLog,
    customRoomReference,
    productImage,
    productAnalysis,
    effectiveHeight,
    effectiveWidth,
    imageAspectRatio,
    framingPreference,
  ]);

  const handleAutoStageEmptyRoom = React.useCallback(() => {
    if (!productImage) return;

    const plan = buildEmptyRoomAutoStagePlan({
      room: roomDimensions,
      interiorType: mappedEmptyRoomInteriorType,
      productAnalysis,
      stagingProfile,
      surfaceOverride: stagingSurfaceOverride,
      productHeightCm: effectiveHeight,
      productWidthCm: effectiveWidth,
      imageAspectRatio,
      framingPreference,
      roomPlacementPreference,
      viewAngle: roomViewAngle,
    });

    if (plan.surfaceDefId && plan.origin && plan.rotation !== undefined) {
      const def = getSceneObjectDefinition(plan.surfaceDefId);
      const instanceId = `${def.id}-${nextSceneObjectIdRef.current++}`;
      const nextState: SceneObjectInstance = {
        instanceId,
        defId: def.id,
        origin: plan.origin,
        rotation: plan.rotation,
        wall: plan.wall ?? "back",
      };
      setSceneObjects([nextState]);
      setSnapSceneObjectId(instanceId);
    } else {
      setSceneObjects([]);
      setSnapSceneObjectId(null);
    }

    setProductPosition(plan.productPosition);
    setSnapLabel(plan.snapLabel);
    setPlacementTarget("product");
    setPreviewInteractionMode("grab");
    setCamera(plan.camera);

    addLog(
      "Empty room auto staging applied",
      JSON.stringify(
        {
          stageKind: plan.stageKind,
          reason: plan.reason,
          surfaceOverride: stagingSurfaceOverride,
          productAnalysis,
          stagingProfile: stagingProfile
            ? {
                support: stagingProfile.suggestedSupport,
                placement: stagingProfile.placementType,
                viewAngle: stagingProfile.viewAngle,
                azimuth: stagingProfile.suggestedAzimuthDeg,
                elevation: stagingProfile.suggestedElevationDeg,
                symmetric: stagingProfile.isSymmetric,
              }
            : null,
          productDimensionsCm: {
            width: effectiveWidth,
            height: effectiveHeight,
            aspectRatio: imageAspectRatio,
          },
          framingPreference,
          surface: plan.snapLabel,
          position: plan.productPosition,
          camera: plan.camera,
        },
        null,
        2
      )
    );
  }, [
    productImage,
    roomDimensions,
    mappedEmptyRoomInteriorType,
    productAnalysis,
    stagingProfile,
    stagingSurfaceOverride,
    effectiveHeight,
    effectiveWidth,
    imageAspectRatio,
    framingPreference,
    roomPlacementPreference,
    roomViewAngle,
    addLog,
  ]);

  // In user mode: auto-stage as soon as the staging profile arrives
  React.useEffect(() => {
    if (userMode && stagingProfile) {
      handleAutoStageEmptyRoom();
    }
  }, [userMode, stagingProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    if (!productImage) return;
    setIsGenerating(true);
    setResultImage(null);
    setLastApiPayload(null);

    try {
      let cleanComposite: string;
      let result: {
        thumbnailUrl: string | null;
        text: string | null;
        prompt: string;
        debugPayload: ApiDebugPayload;
      };
      const startMs = Date.now();

      if (
        isCustomRoomStyle &&
        customRoomLineArt &&
        customRoomReference &&
        effectiveCustomPlacementRect &&
        effectiveCustomCameraRect &&
        customRoomPlan
      ) {
        cleanComposite = await compositeProductOnCustomRoom(
          customRoomLineArt,
          productImage,
          effectiveCustomPlacementRect,
          effectiveCustomCameraRect,
          imageAspectRatio,
          false
        );
        addLog(
          "Generation started",
          includeRoomReferenceInGeneration
            ? "Sending placement preview + product + room reference to backend → gemini-3-pro-image"
            : "Sending placement preview + product only to backend → gemini-3-pro-image"
        );
        addLog(
          "→ Image 1 (Placement)",
          "Custom room line-art composite (clean, no annotations)",
          cleanComposite
        );
        addLog("→ Image 2 (Product)", "Original product photo", productImage);
        if (includeRoomReferenceInGeneration) {
          addLog(
            "→ Image 3 (Room Reference)",
            "Uploaded custom room style reference",
            customRoomReference
          );
        } else {
          addLog(
            "→ Room Reference Omitted",
            "Final generation is using only the line-art placement preview plus the product reference."
          );
        }
        result = await callGeminiForCustomRoom(
          cleanComposite,
          productImage,
          customRoomPlan,
          customPrompt,
          includeRoomReferenceInGeneration,
          customRoomReference,
          effectiveCustomSubjectFraming || customRoomPlan.camera.subjectFraming
        );
      } else {
        const snappedSceneObject = snapSceneObjectId
          ? (sceneObjects.find(
              (instance) => instance.instanceId === snapSceneObjectId
            ) ?? null)
          : null;

        addLog(
          "Generation started",
          "Using the production Decoration image generator with the current demo planner state."
        );
        const productStoragePath = await uploadDataUrlToGCS(
          productImage,
          buildTempPath("decoration_product")
        );
        const decoration = await generateDecorationImage({
          productImage: {
            storagePath: productStoragePath,
            thumbnailUrl: productImage,
            fullUrl: productImage,
            base64: "",
            mimeType: "image/png",
            name: productFileName || "demo-product.png",
          },
          productHeightCm: effectiveHeight,
          productWidthCm: effectiveWidth,
          aspectRatio: "1:1",
          resolution: "1K",
          projectId: "demo",
          productId: "demo-product",
          customPrompt,
          vibe: selectedVibe,
          scene: selectedScene,
          productAnalysis,
          stagingProfile,
          autoStagePlan: {
            stageKind: "demo-empty-room-planner",
            surfaceDefId: snappedSceneObject?.defId ?? null,
            wall: snappedSceneObject?.wall,
            rotation: snappedSceneObject?.rotation,
            origin: snappedSceneObject?.origin,
            productPosition,
            snapLabel,
            camera,
            reason: "Using the current empty-room planner state from the demo.",
          },
        });

        addLog(
          "→ Planner Sketch",
          "Sketch generated by the Decoration service from the current demo planner state.",
          decoration.debug.assets.sketchDataUrl
        );
        addLog(
          "→ Image 1 (Placement)",
          "Composite generated by the Decoration service from the current demo planner state.",
          decoration.debug.assets.compositeDataUrl
        );
        addLog(
          "→ Image 2 (Product)",
          "Original product photo",
          decoration.debug.assets.productReferenceDataUrl
        );

        result = {
          thumbnailUrl:
            decoration.content.thumbnailUrl ||
            decoration.content.fullUrl ||
            decoration.content.image ||
            null,
          text: null,
          prompt:
            typeof decoration.debug.request.params.prompt === "string"
              ? decoration.debug.request.params.prompt
              : "",
          debugPayload: toApiDebugPayload(decoration.debug),
        };
      }
      const durationMs = Date.now() - startMs;

      setLastApiPayload(result.debugPayload);
      addLog("Prompt sent", result.prompt);
      if (result.text) addLog("Gemini text response", result.text);
      if (result.thumbnailUrl) {
        setResultImage(result.thumbnailUrl);
        addLog(
          "Generation complete",
          `Took ${(durationMs / 1000).toFixed(1)}s`,
          result.thumbnailUrl
        );
      } else {
        addLog(
          "Generation returned no image",
          `Took ${(durationMs / 1000).toFixed(1)}s`
        );
      }
    } catch (err: unknown) {
      const errObj = err as Record<string, unknown>;
      const msg = errObj?.message ?? errObj?.code ?? String(err);
      addLog("Generation FAILED", String(msg));
      console.error("[DEMO] Generation error:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              {userMode ? "Height Mode" : "Decoration Debug Mode"}
            </h1>
            {!userMode && (
              <p className="text-xs text-gray-500">
                Place a product in a 3D room with perspective depth. Control the
                camera, position the product, and generate a photorealistic
                scene.
              </p>
            )}
          </div>
          {canToggleMode && (
            <div className="inline-flex rounded-full bg-gray-100 p-0.5">
              <button
                type="button"
                onClick={() => setUserMode(false)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  !userMode
                    ? "bg-gray-900 text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                Dev mode
              </button>
              <button
                type="button"
                onClick={() => setUserMode(true)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  userMode
                    ? "bg-gray-900 text-white"
                    : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                User mode
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden canvas — used for sketch drawing only, not shown in UI */}
      <div className="hidden">
        <canvas ref={sketchCanvasRef} />
      </div>

      <div
        className={`grid h-[calc(100vh-72px)] gap-0 ${userMode ? "grid-cols-[420px_1fr]" : "grid-cols-1 lg:grid-cols-[300px_1fr_360px]"}`}
      >
        {/* ---- Left: Controls ---- */}
        <div className="flex flex-col overflow-y-auto border-r border-gray-200 bg-white p-4">
          <div className="space-y-3">
            {/* Upload product */}
            <Section title="1. Upload Product">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-gray-900 file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-gray-700"
              />
              {productImage && (
                <div className="mt-2 flex items-start gap-2">
                  <img
                    src={productImage}
                    alt="Uploaded product"
                    className="h-20 w-auto rounded border"
                  />
                  <div>
                    <p className="text-xs text-gray-500">{productFileName}</p>
                    <p className="text-xs text-gray-400">
                      Ratio: {imageAspectRatio.toFixed(2)}
                    </p>
                    {isAnalyzingProduct ? (
                      <p className="mt-1 text-[11px] text-gray-400">
                        Analyzing product...
                      </p>
                    ) : stagingProfile ? (
                      <div className="mt-1 space-y-1">
                        <p className="text-[11px] font-medium text-gray-700">
                          {stagingProfile.category}
                        </p>
                        <p className="text-[10px] leading-3.5 text-gray-500">
                          {stagingProfile.description}
                        </p>
                        {!userMode && (
                          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px]">
                            <span className="text-gray-400">View:</span>
                            <span className="text-gray-700">
                              {stagingProfile.viewAngle} /{" "}
                              {stagingProfile.cameraHeight}
                            </span>
                            <span className="text-gray-400">Camera:</span>
                            <span className="text-gray-700">
                              az {stagingProfile.suggestedAzimuthDeg}° / el{" "}
                              {stagingProfile.suggestedElevationDeg}°
                              {stagingProfile.isSymmetric && " (sym)"}
                            </span>
                            <span className="text-gray-400">Staging:</span>
                            <span className="text-gray-700">
                              {stagingProfile.suggestedSupport} (
                              {stagingProfile.placementType})
                            </span>
                            <span className="text-gray-400">Weight:</span>
                            <span className="text-gray-700">
                              {stagingProfile.visualWeight}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : productAnalysis ? (
                      <p className="mt-1 text-[11px] text-gray-500">
                        {productAnalysis.category} ·{" "}
                        {productAnalysis.description}
                      </p>
                    ) : (
                      <p className="mt-1 text-[11px] text-gray-400">
                        Product analysis pending or unavailable.
                      </p>
                    )}
                    {imageTrimmed && (
                      <p className="mt-0.5 text-[10px] font-medium text-emerald-600">
                        ✓ Transparent padding trimmed
                      </p>
                    )}
                  </div>
                </div>
              )}
              <p className="mt-2 text-[10px] leading-4 text-gray-400">
                The uploaded product is analyzed first. Its category is then
                used to guide empty-room auto-staging and placement behavior.
              </p>
            </Section>

            {/* Dimensions */}
            <Section title="2. Product Dimensions">
              <div className="mb-3 flex gap-1 rounded-lg bg-gray-100 p-0.5">
                {(
                  [
                    ["height", "Height only"],
                    ["width", "Width only"],
                    ["both", "Both"],
                  ] as const
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    onClick={() => setDimensionMode(mode)}
                    className={`flex-1 rounded-lg px-2 py-1 text-xs font-medium transition ${
                      dimensionMode === mode
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">
                    Height (cm)
                  </span>
                  {dimensionMode === "width" ? (
                    <div className="mt-1 flex h-[34px] items-center rounded border border-gray-200 bg-gray-50 px-2 text-sm text-gray-500">
                      {effectiveHeight}
                      <span className="ml-1 text-[10px] text-gray-400">
                        auto
                      </span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      min={1}
                      max={roomDimensions.height}
                      value={productHeightCm}
                      onChange={(e) =>
                        setProductHeightCm(Number(e.target.value) || 1)
                      }
                      className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  )}
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">
                    Width (cm)
                  </span>
                  {dimensionMode === "height" ? (
                    <div className="mt-1 flex h-[34px] items-center rounded border border-gray-200 bg-gray-50 px-2 text-sm text-gray-500">
                      {effectiveWidth}
                      <span className="ml-1 text-[10px] text-gray-400">
                        auto
                      </span>
                    </div>
                  ) : (
                    <input
                      type="number"
                      min={1}
                      max={roomDimensions.width}
                      value={productWidthCm}
                      onChange={(e) =>
                        setProductWidthCm(Number(e.target.value) || 1)
                      }
                      className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  )}
                </label>
              </div>
              <p className="mt-1.5 text-[10px] text-gray-400">
                Effective: {effectiveWidth} × {effectiveHeight} cm
              </p>
            </Section>

            {/* Empty room scene */}
            <Section title="3. Scene & Vibe">
              <div className="space-y-2 rounded-xl border border-dashed border-gray-300 p-2">
                {isLoadingBrands ? (
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-[11px] text-gray-500">
                    Loading brand identities...
                  </p>
                ) : globalBrands.length > 0 ? (
                  <>
                    {globalBrands.length > 1 && (
                      <div>
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          Brand identity
                        </p>
                        <CustomSelect
                          options={brandOptions}
                          value={currentBrand?.id || ""}
                          onChange={setSelectedBrandId}
                          buttonClassName="w-full text-left rounded border border-gray-300 bg-white px-2 py-2 text-sm flex items-center justify-between"
                        />
                      </div>
                    )}
                    <div>
                      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Creative vibe
                      </p>
                      <CustomSelect
                        options={vibeOptions}
                        value={selectedVibe?.id || ""}
                        onChange={setSelectedVibeId}
                        buttonClassName="w-full text-left rounded border border-gray-300 bg-white px-2 py-2 text-sm flex items-center justify-between"
                        disabled={vibeOptions.length === 0}
                      />
                    </div>
                    <div>
                      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Scene context
                      </p>
                      <CustomSelect
                        options={sceneOptions}
                        value={selectedScene?.id || ""}
                        onChange={setSelectedSceneId}
                        buttonClassName="w-full text-left rounded border border-gray-300 bg-white px-2 py-2 text-sm flex items-center justify-between"
                        disabled={sceneOptions.length === 0}
                      />
                    </div>
                    <p className="text-[10px] leading-4 text-gray-400">
                      Uses the same brand Scene/Vibe source as Milieu Creative.
                      Auto-stage maps the selected scene to the current
                      empty-room heuristics behind the scenes.
                    </p>
                  </>
                ) : (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 text-[11px] text-amber-700">
                    No brand identities available. Empty-room generation will
                    fall back to generic room defaults.
                  </p>
                )}
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Stage on
                  </p>
                  <div className="grid grid-cols-3 gap-1">
                    {STAGING_SURFACE_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setStagingSurfaceOverride(opt.id)}
                        className={`rounded px-2 py-1 text-xs font-medium transition ${
                          stagingSurfaceOverride === opt.id
                            ? "bg-gray-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Product placement
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {ROOM_PLACEMENT_PREFERENCES.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setRoomPlacementPreference(opt.id)}
                        className={`rounded px-2 py-1 text-xs font-medium transition ${
                          roomPlacementPreference === opt.id
                            ? "bg-gray-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Room view
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {ROOM_VIEW_ANGLES.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setRoomViewAngle(opt.id)}
                        className={`rounded px-2 py-1 text-xs font-medium transition ${
                          roomViewAngle === opt.id
                            ? "bg-gray-700 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleAutoStageEmptyRoom}
                  disabled={!productImage || isAnalyzingProduct}
                  className="w-full rounded bg-gray-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isAnalyzingProduct
                    ? "Analyzing product..."
                    : stagingSurfaceOverride !== "auto"
                      ? `Auto Stage → ${STAGING_SURFACE_OPTIONS.find((o) => o.id === stagingSurfaceOverride)?.label}`
                      : stagingProfile
                        ? "Auto Stage Product (AI)"
                        : "Auto Stage Product"}
                </button>
                <p className="text-[10px] leading-4 text-gray-400">
                  {stagingSurfaceOverride !== "auto"
                    ? "User surface override active. Camera angle still adapts to the product's photographed perspective."
                    : stagingProfile
                      ? "Uses AI staging analysis to choose furniture, placement, and camera angle matching the product photo's perspective."
                      : "Uses keyword heuristics. Upload a product first to enable AI-driven staging with perspective matching."}
                </p>
              </div>
              {/* Shot framing */}
              <div className="mt-2">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Shot framing
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {FRAMING_PREFERENCES.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setFramingPreference(option.id)}
                      className={`rounded px-2 py-1 text-xs font-medium transition ${
                        framingPreference === option.id
                          ? "bg-gray-700 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </Section>

            {/* Custom prompt */}
            <Section title="4. Custom Prompt (optional)">
              <textarea
                rows={3}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Optional — appended to the default prompt as extra instructions..."
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </Section>

            {!userMode && (
              <Section title="5. Room Size">
                <div className="grid grid-cols-3 gap-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">
                      Width
                    </span>
                    <input
                      type="number"
                      min={DEFAULT_ROOM_W}
                      max={800}
                      step={20}
                      value={roomDimensions.width}
                      onChange={(e) =>
                        setRoomDimensions((current) => ({
                          ...current,
                          width: Math.max(
                            DEFAULT_ROOM_W,
                            Number(e.target.value) || DEFAULT_ROOM_W
                          ),
                        }))
                      }
                      className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">
                      Height
                    </span>
                    <input
                      type="number"
                      min={DEFAULT_ROOM_H}
                      max={500}
                      step={10}
                      value={roomDimensions.height}
                      onChange={(e) =>
                        setRoomDimensions((current) => ({
                          ...current,
                          height: Math.max(
                            DEFAULT_ROOM_H,
                            Number(e.target.value) || DEFAULT_ROOM_H
                          ),
                        }))
                      }
                      className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">
                      Depth
                    </span>
                    <input
                      type="number"
                      min={DEFAULT_ROOM_D}
                      max={800}
                      step={20}
                      value={roomDimensions.depth}
                      onChange={(e) =>
                        setRoomDimensions((current) => ({
                          ...current,
                          depth: Math.max(
                            DEFAULT_ROOM_D,
                            Number(e.target.value) || DEFAULT_ROOM_D
                          ),
                        }))
                      }
                      className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                </div>
                <p className="mt-2 text-[10px] leading-4 text-gray-400">
                  Adjusts the room shell, placement bounds, camera target space,
                  and scene-object clamping. Minimum values stay aligned with
                  the built-in furniture layouts.
                </p>
              </Section>
            )}

            {!userMode && (
              <Section title="6. Scene Objects">
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPlacementTarget("product");
                      setPreviewInteractionMode("grab");
                    }}
                    className={`w-full rounded-lg border px-3 py-2.5 text-left text-xs font-medium transition ${
                      placementTarget === "product"
                        ? "border-gray-900 bg-gray-900 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span>Place product</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] ${
                          placementTarget === "product"
                            ? "bg-white/15 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        Grab
                      </span>
                    </div>
                  </button>
                  <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
                    {SCENE_OBJECT_DEFINITIONS.map((def) => {
                      const instanceCount = sceneObjects.filter(
                        (instance) => instance.defId === def.id
                      ).length;
                      return (
                        <div
                          key={def.id}
                          className="rounded-xl border border-gray-200 bg-white p-3"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {def.label}
                              </p>
                              <span
                                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  instanceCount > 0
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                {instanceCount > 0
                                  ? `${instanceCount} added`
                                  : "Available"}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] text-gray-500">
                              {def.placement === "wall"
                                ? "Mounted display surface"
                                : "Freestanding display surface"}
                            </p>
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleAddSceneObject(def)}
                              className="rounded-lg bg-gray-900 px-2 py-1.5 text-[11px] font-medium text-white transition hover:bg-gray-700"
                            >
                              Add another
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {sceneObjects.length > 0 && (
                    <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-2.5">
                      <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Added Instances
                      </p>
                      {sceneObjects.map((instance, index) => {
                        const def = getSceneObjectDefinition(instance.defId);
                        const isSelected =
                          placementTarget ===
                          `scene-object:${instance.instanceId}`;
                        const meta = [
                          def.placement === "wall"
                            ? `Wall: ${instance.wall}`
                            : `Rotation: ${instance.rotation}deg`,
                          `Origin: ${instance.origin[0].toFixed(0)}, ${instance.origin[1].toFixed(0)}, ${instance.origin[2].toFixed(0)}`,
                        ];
                        return (
                          <div
                            key={instance.instanceId}
                            className={`rounded-xl border p-3 transition ${
                              isSelected
                                ? "border-gray-900 bg-gray-50 shadow-sm"
                                : "border-gray-200 bg-white"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {def.label} {index + 1}
                              </p>
                              {isSelected && (
                                <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-white">
                                  Selected
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {meta.map((item) => (
                                <span
                                  key={item}
                                  className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                            <div className="mt-3 grid grid-cols-3 gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setPlacementTarget(
                                    `scene-object:${instance.instanceId}`
                                  );
                                  setPreviewInteractionMode("grab");
                                }}
                                className={`rounded-lg px-2 py-1.5 text-[11px] font-medium transition ${
                                  isSelected
                                    ? "bg-gray-900 text-white"
                                    : "bg-gray-900 text-white hover:bg-gray-700"
                                }`}
                              >
                                Move
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  def.placement === "wall"
                                    ? handleCycleSceneObjectWall(
                                        instance.instanceId
                                      )
                                    : handleRotateSceneObject(
                                        instance.instanceId
                                      )
                                }
                                className="rounded-lg border border-gray-300 px-2 py-1.5 text-[11px] font-medium text-gray-700 transition hover:bg-gray-100"
                              >
                                {def.placement === "wall" ? "Wall" : "Rotate"}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveSceneObject(instance.instanceId)
                                }
                                className="rounded-lg border border-gray-300 px-2 py-1.5 text-[11px] font-medium text-gray-700 transition hover:bg-gray-100"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="px-1 text-[10px] leading-4 text-gray-400">
                    Add any scene object, then use `Grab` or press `G` to place
                    it in the preview. Use `View` or press `V` to orbit and zoom
                    the camera. Added objects become snap surfaces for the
                    product.
                  </p>
                </div>
              </Section>
            )}
          </div>
        </div>
        {/* ---- Middle: Unified Preview (dev only) / Output (user mode) ---- */}
        {userMode ? (
          <div className="flex flex-col items-center justify-center overflow-y-auto bg-gray-50 p-10">
            <div className="w-full max-w-2xl space-y-6">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!compositeDataUrl || isGenerating}
                className="w-full rounded-lg bg-gray-900 px-6 py-4 text-base font-semibold text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Generating...
                  </span>
                ) : (
                  "Generate Scene"
                )}
              </button>
              {isGenerating ? (
                <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white">
                  <div className="text-center">
                    <div className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-900" />
                    <p className="text-sm text-gray-500">
                      Generating with Gemini...
                    </p>
                  </div>
                </div>
              ) : resultImage ? (
                <img
                  src={resultImage}
                  alt="Generated result"
                  className="w-full rounded-xl border border-gray-200 shadow-md"
                />
              ) : (
                <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-sm text-gray-400">
                  Your generated scene will appear here
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 overflow-y-auto p-4">
            <Section title="Debug Mode">
              <div className="mb-3 flex gap-1 rounded-lg bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setPreviewInteractionMode("camera")}
                  className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    previewInteractionMode === "camera"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  View (V)
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewInteractionMode("grab")}
                  className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                    previewInteractionMode === "grab"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Grab (G)
                </button>
              </div>
              <UnifiedPreview
                sketchDataUrl={sketchDataUrl}
                compositeDataUrl={compositeDataUrl}
                onDragStart={handleOrbitDragStart}
                onCameraOrbit={handleCameraOrbit}
                onCameraZoom={handleCameraZoom}
                onPlacement={handlePlacement}
                onSelectTarget={handleSelectTarget}
                onSelectSceneObject={handleSelectSceneObject}
                interactionMode={previewInteractionMode}
                productOverlay={productOverlay}
                sceneObjectOverlays={sceneObjectOverlays}
                selectedTarget={placementTarget}
                placementLabel={
                  placementTarget === "none"
                    ? "nothing selected"
                    : placementTarget === "product"
                      ? "product"
                      : selectedSceneObject
                        ? getSceneObjectDefinition(selectedSceneObject.defId)
                            .label
                        : "scene object"
                }
              />
              <>
                {/* Camera sliders — only relevant in 3D mode */}
                <div className="mt-3 grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-2">
                  <label className="text-[10px] font-medium text-gray-500">
                    Pan X
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={roomDimensions.width}
                    step={5}
                    value={camera.target[0]}
                    onChange={(e) =>
                      setCamera((c) => ({
                        ...c,
                        target: [
                          Number(e.target.value),
                          c.target[1],
                          c.target[2],
                        ],
                      }))
                    }
                    className="h-1.5 w-full cursor-pointer accent-gray-900"
                  />
                  <span className="w-10 text-right text-[10px] tabular-nums text-gray-500">
                    {camera.target[0].toFixed(0)}cm
                  </span>

                  <label className="text-[10px] font-medium text-gray-500">
                    Pan Z
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={roomDimensions.depth}
                    step={5}
                    value={camera.target[2]}
                    onChange={(e) =>
                      setCamera((c) => ({
                        ...c,
                        target: [
                          c.target[0],
                          c.target[1],
                          Number(e.target.value),
                        ],
                      }))
                    }
                    className="h-1.5 w-full cursor-pointer accent-gray-900"
                  />
                  <span className="w-10 text-right text-[10px] tabular-nums text-gray-500">
                    {camera.target[2].toFixed(0)}cm
                  </span>

                  <label className="text-[10px] font-medium text-gray-500">
                    FOV
                  </label>
                  <input
                    type="range"
                    min={20}
                    max={90}
                    step={1}
                    value={camera.fov}
                    onChange={(e) =>
                      setCamera((c) => ({
                        ...c,
                        fov: Number(e.target.value),
                      }))
                    }
                    className="h-1.5 w-full cursor-pointer accent-gray-900"
                  />
                  <span className="w-10 text-right text-[10px] tabular-nums text-gray-500">
                    {camera.fov}°
                  </span>

                  <label className="text-[10px] font-medium text-gray-500">
                    Height
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={roomDimensions.height}
                    step={5}
                    value={camera.target[1]}
                    onChange={(e) =>
                      setCamera((c) => ({
                        ...c,
                        target: [
                          c.target[0],
                          Number(e.target.value),
                          c.target[2],
                        ],
                      }))
                    }
                    className="h-1.5 w-full cursor-pointer accent-gray-900"
                  />
                  <span className="w-10 text-right text-[10px] tabular-nums text-gray-500">
                    {camera.target[1]}cm
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <p className="text-[10px] text-gray-400">
                    <>
                      product: ({productPosition.x.toFixed(0)},{" "}
                      {productPosition.z.toFixed(0)}) cm ·{" "}
                      <span
                        className={
                          snapLabel === "floor"
                            ? "text-gray-400"
                            : "font-medium text-indigo-500"
                        }
                      >
                        {snapLabel}
                        {productPosition.y > 0
                          ? ` +${productPosition.y}cm`
                          : ""}
                      </span>
                      {" · "}cam target ({camera.target[0].toFixed(0)},{" "}
                      {camera.target[2].toFixed(0)}) · az{" "}
                      {camera.azimuth.toFixed(0)}° el{" "}
                      {camera.elevation.toFixed(0)}°
                    </>
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setProductPosition(
                          defaultProductPosForRoom(roomDimensions)
                        );
                        setSnapLabel("floor");
                        setSnapSceneObjectId(null);
                      }}
                      className="text-[10px] text-gray-400 underline hover:text-gray-700"
                    >
                      Reset product
                    </button>
                    <button
                      onClick={() => {
                        setCamera(defaultCameraForRoom(roomDimensions));
                      }}
                      className="text-[10px] text-gray-400 underline hover:text-gray-700"
                    >
                      Reset camera
                    </button>
                  </div>
                </div>
                {isDev && lastApiPayload && (
                  <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      API Payload Downloads
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          downloadJsonFile(
                            lastApiPayload.request,
                            "decoration-api-request.json"
                          )
                        }
                        className="rounded border border-gray-300 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50"
                      >
                        Download request JSON
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          downloadTextFile(
                            lastApiPayload.prompt,
                            "decoration-prompt.txt"
                          )
                        }
                        className="rounded border border-gray-300 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50"
                      >
                        Download prompt
                      </button>
                      {lastApiPayload.assets.map((asset) => (
                        <button
                          key={asset.filename}
                          type="button"
                          onClick={() =>
                            downloadDataUrl(asset.dataUrl, asset.filename)
                          }
                          className="rounded border border-gray-300 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                          Download {asset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!compositeDataUrl || isGenerating}
                className="mt-3 w-full rounded bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isGenerating ? "Generating..." : "Generate Image"}
              </button>
            </Section>

            <Section title="Generated Result">
              {isGenerating ? (
                <div className="flex h-48 items-center justify-center rounded border border-dashed border-gray-300">
                  <div className="text-center">
                    <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
                    <p className="text-sm text-gray-500">
                      Generating with Gemini...
                    </p>
                  </div>
                </div>
              ) : resultImage ? (
                <img
                  src={resultImage}
                  alt="Generated result"
                  className="w-full max-w-lg rounded border border-gray-200"
                />
              ) : (
                <div className="flex h-48 items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-400">
                  Result will appear here
                </div>
              )}
            </Section>
          </div>
        )}{" "}
        {/* end dev-mode middle column */}
        {/* ---- Right: Debug Log (dev only) ---- */}
        {!userMode && (
          <div className="overflow-y-auto border-l border-gray-200 bg-gray-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-700">
              Debug Log
            </h3>
            <div className="space-y-2">
              {logs.length === 0 && (
                <p className="text-xs text-gray-400">No events yet.</p>
              )}
              {logs.map((log, i) => (
                <div
                  key={i}
                  className="rounded border border-gray-200 bg-white p-2"
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[10px] text-gray-400">
                      {log.time}
                    </span>
                    <span className="text-xs font-semibold text-gray-700">
                      {log.label}
                    </span>
                  </div>
                  {log.detail && (
                    <p className="mt-1 whitespace-pre-wrap break-all text-[11px] text-gray-500">
                      {log.detail}
                    </p>
                  )}
                  {log.imageDataUrl && (
                    <img
                      src={log.imageDataUrl}
                      alt={log.label}
                      className="mt-2 max-h-40 rounded border"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CameraSlider({
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="mb-2 block">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-medium text-gray-700">{label}</span>
        <span className="text-[10px] tabular-nums text-gray-400">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-0.5 h-1.5 w-full cursor-pointer accent-gray-900"
      />
    </label>
  );
}

/** Single preview canvas that routes:
 *  - Drag (> 5 px)   → camera orbit
 *  - Click (≤ 5 px)  → place product at that room position
 *  - Scroll wheel    → camera distance
 */
/** Single preview canvas that routes:
 *  - Drag (> 5 px)   → camera orbit (shows sketchDataUrl for instant feedback)
 *  - Release drag    → switches back to compositeDataUrl
 *  - Click (≤ 5 px)  → place product at that room position
 *  - Scroll wheel    → camera distance
 *
 *  The key performance insight: composite re-rendering is async (loads two images onto a
 *  canvas), so during active orbit we display the synchronously-updated sketch instead.
 *  This makes the camera feel instant while the richer composite catches up on release.
 */
function UnifiedPreview({
  sketchDataUrl,
  compositeDataUrl,
  onDragStart,
  onCameraOrbit,
  onCameraZoom,
  onPlacement,
  onSelectTarget,
  onSelectSceneObject,
  interactionMode,
  productOverlay,
  sceneObjectOverlays,
  selectedTarget,
  placementLabel,
}: {
  sketchDataUrl: string | null;
  compositeDataUrl: string | null;
  onDragStart: () => void;
  onCameraOrbit: (totalDx: number, totalDy: number) => void;
  onCameraZoom: (deltaY: number) => void;
  onPlacement: (
    fracX: number,
    fracY: number,
    commit?: boolean,
    targetOverride?: PlacementTarget
  ) => void;
  onSelectTarget: (target: PlacementTarget) => void;
  onSelectSceneObject: (objectId: string) => void;
  interactionMode: PreviewInteractionMode;
  productOverlay: SceneObjectOverlay | null;
  sceneObjectOverlays: SceneObjectOverlay[];
  selectedTarget: PlacementTarget;
  placementLabel: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const pointerState = React.useRef<{
    startX: number;
    startY: number;
    moved: boolean;
    pointerId: number;
    dragTarget: PlacementTarget | null;
  } | null>(null);

  // Use latest callbacks in wheel handler without re-registering on every render
  const onCameraZoomRef = React.useRef(onCameraZoom);
  onCameraZoomRef.current = onCameraZoom;

  // Non-passive wheel listener so we can call preventDefault
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (interactionMode !== "camera") return;
      e.preventDefault();
      onCameraZoomRef.current(e.deltaY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [interactionMode]);

  const getFrac = (clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    };
  };

  const getTargetAtFrac = React.useCallback(
    (fracX: number, fracY: number) => {
      const hits = [
        ...(productOverlay ? [productOverlay] : []),
        ...sceneObjectOverlays,
      ].filter(
        (overlay) =>
          fracX >= overlay.left &&
          fracX <= overlay.left + overlay.width &&
          fracY >= overlay.top &&
          fracY <= overlay.top + overlay.height
      );
      if (hits.length === 0) return null;
      hits.sort((a, b) => a.width * a.height - b.width * b.height);
      const selectedHit =
        selectedTarget === "product"
          ? hits.find((hit) => hit.id === "product")
          : selectedTarget === "none"
            ? null
            : hits.find(
                (hit) => hit.id === selectedTarget.replace("scene-object:", "")
              );
      return selectedHit ?? hits[0];
    },
    [productOverlay, sceneObjectOverlays, selectedTarget]
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const frac = getFrac(e.clientX, e.clientY);
    let dragTarget: PlacementTarget | null = null;
    if (interactionMode === "grab" && frac) {
      const hitTarget = getTargetAtFrac(frac.x, frac.y);
      if (hitTarget?.id === "product") {
        onSelectTarget("product");
        dragTarget = "product";
      } else if (hitTarget) {
        onSelectSceneObject(hitTarget.id);
        dragTarget = `scene-object:${hitTarget.id}`;
      } else {
        onSelectTarget("none");
        dragTarget = null;
      }
    }
    pointerState.current = {
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      pointerId: e.pointerId,
      dragTarget,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    if (interactionMode === "camera") onDragStart();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const ps = pointerState.current;
    if (!ps || ps.pointerId !== e.pointerId) return;
    const dx = e.clientX - ps.startX;
    const dy = e.clientY - ps.startY;
    if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    if (!ps.moved) {
      ps.moved = true;
      if (interactionMode === "camera") {
        setIsDragging(true); // switches display to fast sketch
      }
    }
    if (interactionMode === "camera") {
      onCameraOrbit(dx, dy);
      return;
    }
    const f = getFrac(e.clientX, e.clientY);
    if (f && ps.dragTarget) {
      setIsDragging(true);
      onPlacement(f.x, f.y, false, ps.dragTarget);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const ps = pointerState.current;
    if (!ps || ps.pointerId !== e.pointerId) return;
    if (interactionMode === "grab") {
      const f = getFrac(e.clientX, e.clientY);
      if (ps.moved && f && ps.dragTarget) {
        onPlacement(f.x, f.y, true, ps.dragTarget);
      }
      setIsDragging(false);
    } else if (ps.moved) {
      setIsDragging(false); // switches back to composite
    }
    pointerState.current = null;
  };

  // During drag: show the synchronously-updated sketch (instant response).
  // After drag:  show the composite (product overlaid) once it catches up.
  const displayUrl =
    interactionMode === "camera" && isDragging
      ? (sketchDataUrl ?? compositeDataUrl)
      : (compositeDataUrl ?? sketchDataUrl);

  return (
    <div
      ref={containerRef}
      className={`relative w-full select-none overflow-hidden rounded border border-gray-200 bg-gray-50 ${
        interactionMode === "camera"
          ? isDragging
            ? "cursor-grabbing"
            : "cursor-grab"
          : isDragging
            ? "cursor-grabbing"
            : "cursor-crosshair"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {displayUrl ? (
        <>
          <img
            src={displayUrl}
            alt="Room preview"
            className="pointer-events-none mx-auto block h-auto max-h-[min(38vh,360px)] w-auto max-w-full"
            draggable={false}
          />
          {[
            ...(productOverlay ? [productOverlay] : []),
            ...sceneObjectOverlays,
          ].map((overlay) => {
            const isSelected =
              (overlay.id === "product" && selectedTarget === "product") ||
              selectedTarget === `scene-object:${overlay.id}`;
            return (
              <div
                key={overlay.id}
                className={`pointer-events-none absolute rounded border transition ${
                  isSelected
                    ? "border-indigo-500 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(99,102,241,0.4)]"
                    : "border-white/70 bg-white/5"
                }`}
                style={{
                  left: `${overlay.left * 100}%`,
                  top: `${overlay.top * 100}%`,
                  width: `${overlay.width * 100}%`,
                  height: `${overlay.height * 100}%`,
                }}
              >
                <div
                  className={`absolute -top-5 left-0 rounded px-1.5 py-0.5 text-[10px] ${
                    isSelected
                      ? "bg-indigo-500 text-white"
                      : "bg-black/55 text-white"
                  }`}
                >
                  {overlay.label}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="flex aspect-[4/3] max-h-[min(38vh,360px)] w-full items-center justify-center text-sm text-gray-400">
          Upload a product to begin
        </div>
      )}
      {/* Interaction hint */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-black/50 px-2 py-0.5 text-[10px] text-white whitespace-nowrap">
        {interactionMode === "camera"
          ? "V view mode · drag to orbit · scroll to zoom"
          : `G grab mode · drag highlighted ${placementLabel}`}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">{title}</h3>
      {children}
    </div>
  );
}
